import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.164.1/+esm';
import { RoundedBoxGeometry } from 'https://cdn.jsdelivr.net/npm/three@0.164.1/examples/jsm/geometries/RoundedBoxGeometry.js/+esm';
// Inject the museum's fonts, styles and markup before the museum code looks them up.
(() => {
  const font = document.createElement('link'); font.rel = 'stylesheet'; font.href = "https://fonts.googleapis.com/css2?family=IM+Fell+English:ital@0;1&family=IM+Fell+English+SC&family=Spectral:ital,wght@0,400;0,600;1,400&family=Special+Elite&display=swap";
  const style = document.createElement('style'); style.textContent = "\n/* Layout: one full-bleed WebGL interior; brass, walnut and kraft overlays float above it.\n   Single dark look on purpose: the museum is lit by candles. */\n:root {\n  color-scheme: dark;\n  --night: #0b0806;\n  --walnut: #23160e;\n  --walnut-2: #33210f;\n  --brass: #c9a35f;\n  --brass-deep: #7d5f31;\n  --kraft: #cdb088;\n  --kraft-2: #bf9f74;\n  --ink: #1d140d;\n  --seal: #b8342a;\n  --vellum: #eadfc8;\n  --vellum-dim: #b9a98d;\n  --display: \"IM Fell English SC\", \"IM Fell English\", Georgia, serif;\n  --display-i: \"IM Fell English\", Georgia, serif;\n  --serif: \"Spectral\", Georgia, \"Times New Roman\", serif;\n  --stamp: \"Special Elite\", \"Courier New\", monospace;\n}\n#app, #app *, #app *::before, #app *::after { box-sizing: border-box; }\n#app [hidden] { display: none !important; }\n#app p, #app h1, #app h2, #app dl { margin: 0; }\n#app button { margin: 0; text-transform: none; min-height: 0; }\nhtml, body { height: 100%; }\nbody { background: var(--night); color: var(--vellum); font-family: var(--serif); font-size: 15px; overflow: hidden; -webkit-tap-highlight-color: transparent; }\n#app { position: fixed; inset: 0; z-index: 2147483000; background: var(--night); color: var(--vellum); font-family: var(--serif); font-size: 15px; line-height: normal; text-align: left; overflow: hidden; user-select: none; -webkit-user-select: none; }\n#c { position: absolute; inset: 0; width: 100%; height: 100%; display: block; touch-action: none; outline: none; }\n#vignette { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 50% 46%, transparent 52%, rgba(6, 3, 2, .62) 100%); }\n#fade { position: absolute; inset: 0; background: var(--night); opacity: 0; pointer-events: none; transition: opacity .28s ease; }\n#fade.on { opacity: 1; }\nbutton { font: inherit; color: inherit; cursor: pointer; }\nbutton:focus-visible, input:focus-visible, a:focus-visible { outline: 2px solid var(--brass); outline-offset: 2px; }\n\n/* HUD */\n#hud { position: absolute; left: 0; right: 0; top: 0; padding: calc(env(safe-area-inset-top, 0px) + 14px) 16px 0; display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; pointer-events: none; }\n.where { display: grid; gap: 2px; min-width: 0; max-width: 60%; text-shadow: 0 1px 8px rgba(0,0,0,.9); }\n.where-name { font-family: var(--display); font-size: 19px; letter-spacing: .04em; color: var(--vellum); }\n.where-sub { font-family: var(--stamp); font-size: 12.5px; color: var(--brass); letter-spacing: .04em; }\n.tools { display: flex; gap: 8px; pointer-events: auto; flex-wrap: wrap; justify-content: flex-end; }\n.tool { display: inline-block; background: rgba(20, 12, 7, .72); border: 1px solid rgba(201, 163, 95, .55); color: var(--vellum); border-radius: 3px; padding: 7px 12px; font-family: var(--display); font-size: 14px; letter-spacing: .05em; backdrop-filter: blur(6px); }\n.tool:hover { border-color: var(--brass); color: #fff4dc; }\nkbd { font-family: var(--stamp); font-size: 11px; color: var(--brass); margin-left: 6px; }\n#crosshair { position: absolute; left: 50%; top: 50%; width: 6px; height: 6px; margin: -3px 0 0 -3px; border-radius: 50%; background: rgba(240, 220, 180, .75); box-shadow: 0 0 0 1px rgba(0,0,0,.5); pointer-events: none; transition: transform .15s ease, background .15s ease; }\n#crosshair.on { transform: scale(1.8); background: var(--brass); }\n#hint { position: absolute; left: 50%; top: 58%; transform: translateX(-50%); font-family: var(--display-i); font-style: italic; font-size: 17px; color: var(--vellum); text-shadow: 0 1px 10px #000; pointer-events: none; transition: opacity .4s ease; white-space: nowrap; }\n\n/* Hover placard: a kraft museum label */\n#placard { position: absolute; left: 50%; bottom: calc(env(safe-area-inset-bottom, 0px) + 22px); transform: translateX(-50%); background: var(--kraft); color: var(--ink); border-radius: 2px; padding: 10px 16px 9px; display: grid; gap: 2px; justify-items: center; box-shadow: 0 10px 30px rgba(0,0,0,.55), inset 0 0 0 1px rgba(60,40,20,.25); pointer-events: none; max-width: calc(100% - 32px); }\n#placard .no { font-family: var(--stamp); color: var(--seal); font-size: 17px; letter-spacing: .06em; }\n#placard .meta { font-size: 13.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }\n#placard .act { font-family: var(--display-i); font-style: italic; font-size: 12.5px; color: #5a4128; }\n\n/* Overlays */\n.overlay { position: absolute; inset: 0; display: grid; grid-template-columns: minmax(0, 1fr); place-items: center; padding: 16px; padding-block: calc(env(safe-area-inset-top, 0px) + 16px) calc(env(safe-area-inset-bottom, 0px) + 16px); overflow-y: auto; }\n#intro { background: radial-gradient(ellipse at 50% 40%, rgba(11, 8, 6, .35), rgba(11, 8, 6, .9) 75%); transition: opacity .6s ease; }\n#intro.leaving { opacity: 0; pointer-events: none; }\n.intro-card { width: min(560px, 100%); min-width: 0; text-align: center; display: grid; gap: 14px; justify-items: center; }\n.eyebrow { font-family: var(--display); letter-spacing: .32em; font-size: 13px; color: var(--brass); margin: 0; padding-left: .32em; }\n.intro-card h1 { font-family: var(--display-i); font-style: italic; font-weight: 400; font-size: clamp(54px, 13vw, 96px); line-height: .95; margin: 0; color: #f4e6c9; text-wrap: balance; text-shadow: 0 2px 30px rgba(0,0,0,.8); }\n.lede { font-size: 16.5px; line-height: 1.55; max-width: 44ch; margin: 0; color: var(--vellum); text-wrap: pretty; }\n.ledger { display: flex; gap: 0; border-top: 1px solid rgba(201,163,95,.45); border-bottom: 1px solid rgba(201,163,95,.45); width: 100%; max-width: 440px; }\n.ledger div { flex: 1; display: grid; gap: 2px; padding: 10px 6px; }\n.ledger div + div { border-left: 1px solid rgba(201,163,95,.3); }\n.ledger b { font-family: var(--stamp); font-weight: 400; font-size: 19px; color: #f4e6c9; font-variant-numeric: tabular-nums; }\n.ledger span { font-family: var(--display); font-size: 12px; letter-spacing: .14em; color: var(--brass); }\n.progress { width: 100%; max-width: 320px; height: 2px; background: rgba(201,163,95,.2); overflow: hidden; }\n#bar { height: 100%; width: 0; background: var(--brass); transition: width .3s ease; }\n.status { margin: 0; font-family: var(--display-i); font-style: italic; color: var(--vellum-dim); font-size: 14.5px; min-height: 1.4em; }\n#enter { background: linear-gradient(#d6b26c, #a98545); color: #24170a; border: 1px solid #5d4420; border-radius: 3px; padding: 13px 30px; font-family: var(--display); font-size: 19px; letter-spacing: .08em; box-shadow: 0 6px 24px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,240,200,.6); }\n#enter:disabled { opacity: .45; cursor: progress; }\n#enter:not(:disabled):hover { filter: brightness(1.08); }\n.keys { margin: 0; font-size: 13px; color: var(--vellum-dim); line-height: 1.6; max-width: 46ch; }\n.touch-only { display: none; }\n.is-touch .touch-only { display: block; }\n.is-touch .desk-only { display: none; }\n\n/* Inspect: the drawing large, with its kraft wall label */\n#inspect { background: rgba(8, 5, 3, .93); }\n.inspect-wrap { min-width: 0; display: flex; gap: 28px; align-items: center; justify-content: center; width: min(1100px, 100%); }\n.inspect-art { flex: 1 1 auto; min-width: 0; display: grid; place-items: center; }\n#inspect-canvas { display: block; max-width: 100%; box-shadow: 0 0 0 10px #e7dcc4, 0 0 0 22px #1b1512, 0 24px 60px rgba(0,0,0,.8); background: #2a1d13; }\n.label { flex: 0 0 300px; min-width: 0; background: var(--kraft); color: var(--ink); padding: 20px 20px 18px; border-radius: 2px; box-shadow: 0 16px 40px rgba(0,0,0,.6); display: grid; gap: 10px; }\n.label .stamp { font-family: var(--stamp); color: var(--seal); font-size: 22px; margin: 0; letter-spacing: .05em; }\n.label h2 { margin: 0; font-family: var(--display-i); font-style: italic; font-weight: 400; font-size: 20px; line-height: 1.2; text-wrap: balance; }\n.label h2:empty { display: none; }\n.label dl { display: grid; grid-template-columns: auto 1fr; gap: 3px 12px; margin: 0; font-size: 13.5px; }\n.label dt { font-family: var(--display); letter-spacing: .1em; font-size: 12px; color: #5a4128; padding-top: 1px; }\n.label dd { margin: 0; }\n.label .desc { margin: 0; font-size: 14px; line-height: 1.5; }\n.label .subjects { margin: 0; font-size: 12.5px; font-style: italic; color: #4a3520; }\n.lb-nav { display: flex; gap: 8px; }\n.lb-nav button, #lb-close { flex: 1; background: transparent; border: 1px solid rgba(29,20,13,.45); color: var(--ink); border-radius: 2px; padding: 7px 8px; font-family: var(--display); letter-spacing: .05em; font-size: 14px; }\n.lb-nav button:hover, #lb-close:hover { background: rgba(29,20,13,.08); }\n#lb-close { background: var(--ink); color: var(--kraft); border-color: var(--ink); }\n#lb-close:hover { background: #2e2116; }\n.lb-links { display: grid; gap: 4px; margin: 0; font-size: 13px; }\n.lb-links a { color: #6b1f17; }\n\n/* Directory */\n#directory { background: rgba(8, 5, 3, .78); }\n.dir-card { width: min(520px, 100%); min-width: 0; background: linear-gradient(var(--walnut-2), var(--walnut)); border: 1px solid rgba(201,163,95,.5); border-radius: 3px; padding: 18px 20px 20px; box-shadow: 0 20px 60px rgba(0,0,0,.7); display: grid; gap: 16px; }\n.dir-card header { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }\n.dir-card h2 { margin: 0; font-family: var(--display); font-weight: 400; font-size: 24px; letter-spacing: .12em; color: var(--brass); }\n#find { display: grid; gap: 6px; }\n#find label { font-family: var(--display); font-size: 13px; letter-spacing: .1em; color: var(--vellum-dim); }\n.find-row { display: flex; gap: 8px; align-items: center; }\n.find-row span { font-family: var(--stamp); color: var(--seal); font-size: 18px; filter: brightness(1.35); }\n#find-no { flex: 1; min-width: 0; background: #120b07; border: 1px solid rgba(201,163,95,.45); color: #f4e6c9; border-radius: 2px; padding: 8px 10px; font-family: var(--stamp); font-size: 18px; letter-spacing: .1em; }\n#find-go { background: var(--brass); color: #24170a; border: 0; border-radius: 2px; padding: 9px 16px; font-family: var(--display); font-size: 15px; letter-spacing: .06em; }\n#find-msg { margin: 0; font-size: 13px; color: var(--vellum-dim); min-height: 1.3em; }\n#rooms { list-style: none; margin: 0; padding: 0; display: grid; gap: 2px; border-top: 1px solid rgba(201,163,95,.3); }\n#rooms button { width: 100%; display: grid; grid-template-columns: 2.4em minmax(0, 1fr) auto; gap: 10px; align-items: baseline; text-align: left; background: transparent; border: 0; border-bottom: 1px solid rgba(201,163,95,.18); padding: 10px 4px; color: var(--vellum); }\n#rooms button:hover { background: rgba(201,163,95,.08); }\n#rooms .rn { font-family: var(--display); color: var(--brass); letter-spacing: .06em; }\n#rooms .rt { font-family: var(--display); font-size: 17px; letter-spacing: .03em; min-width: 0; }\n#rooms .rt small { display: block; font-family: var(--stamp); font-size: 12px; color: var(--vellum-dim); letter-spacing: .03em; margin-top: 2px; }\n#rooms .rc { font-family: var(--stamp); font-size: 13px; color: var(--vellum-dim); font-variant-numeric: tabular-nums; }\n#rooms li.sub button { padding: 5px 4px 5px 0; }\n#rooms li.sub .rt { font-size: 15px; color: var(--vellum-dim); }\n#rooms li.sub .rt small { display: inline; margin-left: 8px; }\n#rooms .here .rt::after { content: \" \u00b7 you are here\"; font-family: var(--display-i); font-style: italic; font-size: 13px; color: var(--brass); letter-spacing: 0; }\n\n/* Touch joystick */\n#joy { position: absolute; left: calc(env(safe-area-inset-left, 0px) + 22px); bottom: calc(env(safe-area-inset-bottom, 0px) + 26px); width: 118px; height: 118px; border-radius: 50%; border: 1px solid rgba(201,163,95,.5); background: radial-gradient(circle, rgba(30,18,10,.35), rgba(30,18,10,.6)); touch-action: none; }\n#joy-knob { position: absolute; left: 50%; top: 50%; width: 46px; height: 46px; margin: -23px 0 0 -23px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #e0bf7d, #8d6a35); box-shadow: 0 4px 12px rgba(0,0,0,.6); }\n.is-touch #placard { bottom: calc(env(safe-area-inset-bottom, 0px) + 160px); }\n\n@media (max-width: 760px) {\n  .inspect-wrap { flex-direction: column; gap: 22px; }\n  .label { flex: 0 0 auto; width: 100%; }\n  .where-name { font-size: 16px; }\n  .tool { padding: 6px 10px; font-size: 13px; }\n  kbd { display: none; }\n}\n@media (prefers-reduced-motion: reduce) {\n  #fade, #intro, #bar, #crosshair { transition: none; }\n}\n";
  document.head.append(font, style);
  const holder = document.createElement('div'); holder.innerHTML = "<div id=\"app\">\n  <canvas id=\"c\" tabindex=\"0\" aria-label=\"The museum, a walkable 3D gallery\"></canvas>\n  <div id=\"vignette\"></div>\n  <div id=\"hud\" hidden>\n    <div class=\"where\"><span class=\"where-name\" id=\"where-name\">The Reading Room</span><span class=\"where-sub\" id=\"where-sub\"></span></div>\n    <div class=\"tools\">\n      <a class=\"tool\" href=\"/pages/archive\" style=\"text-decoration:none\">Archive</a>\n      <button class=\"tool\" id=\"btn-dir\" type=\"button\">Directory<kbd>M</kbd></button>\n      <button class=\"tool\" id=\"btn-sound\" type=\"button\" aria-pressed=\"false\">Sound off</button>\n    </div>\n  </div>\n  <div id=\"crosshair\" hidden></div>\n  <div id=\"hint\" hidden>Click to look around</div>\n  <div id=\"placard\" hidden><span class=\"no\" id=\"pl-no\"></span><span class=\"meta\" id=\"pl-meta\"></span><span class=\"act\" id=\"pl-act\"></span></div>\n  <div id=\"joy\" hidden><div id=\"joy-knob\"></div></div>\n\n  <div id=\"intro\" class=\"overlay\">\n    <div class=\"intro-card\">\n      <p class=\"eyebrow\">Super Extra Fancy</p>\n      <h1>The Museum</h1>\n      <p class=\"lede\">Every drawing Cole Skaggs has stamped, hung at true size in number order. Start among the first thousand by the fire, walk the Long Gallery to this week, and step off into the Small, Medium and Large galleries.</p>\n      <div class=\"ledger\">\n        <div><b id=\"st-count\">8,429</b><span>numbered</span></div>\n        <div><b id=\"st-early\">1,986</b><span>early work</span></div>\n        <div><b id=\"st-rooms\">6</b><span>rooms</span></div>\n      </div>\n      <div class=\"progress\"><div id=\"bar\"></div></div>\n      <p class=\"status\" id=\"status\">Unlocking the doors\u2026</p>\n      <button id=\"enter\" type=\"button\" disabled>Enter the museum</button>\n      <p class=\"keys desk-only\">Click to look around \u00b7 W A S D to walk \u00b7 Shift to hurry \u00b7 click a drawing to read its label \u00b7 M for the directory</p>\n      <p class=\"keys touch-only\">Left thumb walks \u00b7 drag anywhere to look \u00b7 tap a drawing to read its label</p>\n    </div>\n  </div>\n\n  <div id=\"inspect\" class=\"overlay\" hidden>\n    <div class=\"inspect-wrap\">\n      <div class=\"inspect-art\"><canvas id=\"inspect-canvas\" width=\"10\" height=\"10\"></canvas></div>\n      <aside class=\"label\" aria-live=\"polite\">\n        <p class=\"stamp\" id=\"lb-no\"></p>\n        <h2 id=\"lb-title\"></h2>\n        <dl>\n          <dt>Year</dt><dd id=\"lb-year\"></dd>\n          <dt>Size</dt><dd id=\"lb-size\"></dd>\n          <dt>Medium</dt><dd id=\"lb-medium\"></dd>\n          <dt>Hung in</dt><dd id=\"lb-where\"></dd>\n        </dl>\n        <p class=\"desc\" id=\"lb-desc\"></p>\n        <p class=\"subjects\" id=\"lb-subj\"></p>\n        <div class=\"lb-nav\"><button type=\"button\" id=\"lb-prev\">\u2190 Previous</button><button type=\"button\" id=\"lb-next\">Next \u2192</button></div>\n        <p class=\"lb-links\">\n          <a href=\"https://www.sxf.art/pages/archive\" target=\"_blank\" rel=\"noopener\">Browse the full-size archive on sxf.art</a>\n          <a href=\"https://www.sxf.art/products/custom-artwork\" target=\"_blank\" rel=\"noopener\">Commission a drawing in this spirit</a>\n        </p>\n        <button type=\"button\" id=\"lb-close\">Back to the gallery</button>\n      </aside>\n    </div>\n  </div>\n\n  <div id=\"directory\" class=\"overlay\" hidden>\n    <div class=\"dir-card\" role=\"dialog\" aria-label=\"Directory\">\n      <header><h2>Directory</h2><button class=\"tool\" id=\"dir-close\" type=\"button\">Close</button></header>\n      <form id=\"find\" autocomplete=\"off\">\n        <label for=\"find-no\">Walk to a drawing by its number</label>\n        <div class=\"find-row\"><span>No.</span><input id=\"find-no\" inputmode=\"numeric\" maxlength=\"6\" placeholder=\"000245\"><button id=\"find-go\" type=\"submit\">Go</button></div>\n        <p id=\"find-msg\" role=\"status\"></p>\n      </form>\n      <ol id=\"rooms\"></ol>\n    </div>\n  </div>\n  <div id=\"fade\"></div>\n</div>\n\n";
  document.body.append(...holder.childNodes);
  document.getElementById('sxf-museum-boot')?.remove();
})();
const FILES = 'https://cdn.shopify.com/s/files/1/0622/9164/0486/files/sxf-museum-v3-';
const ASSET = (p) => FILES + (p.startsWith('data/') ? p.slice(5).replace(/\.json$/, '.txt') : p.replace(/^art\//, '').replace(/\//g, '-'));
document.documentElement.style.overflow = 'hidden'; document.body.style.overflow = 'hidden';

/* ------------------------------------------------------------------ setup */
const $ = (s) => document.querySelector(s);
const IS_TOUCH = matchMedia('(pointer: coarse)').matches && !matchMedia('(pointer: fine)').matches;
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
if (IS_TOUCH) document.documentElement.classList.add('is-touch');
const Q = {
  dpr: Math.min(window.devicePixelRatio || 1, IS_TOUCH ? 1.5 : 1.75),
  lights: IS_TOUCH ? 4 : 6,
  hiRadius: IS_TOUCH ? 5.5 : 8.5,
  hiMax: IS_TOUCH ? 8 : 18,
};

/* Museum dimensions, metres. The aisle runs toward -Z. */
const EYE = 1.62, AISLE = 5.2, HALF = AISLE / 2, ALC_D = 4.0, OUT_X = HALF + ALC_D;
const ALC_W = 4.2, PART_T = 0.42, PITCH = ALC_W + PART_T, PAD = 2.6, CEIL = 6.6;
const PART_H = 4.5, WALL_T = 0.6, DOOR_W = 2.4, DOOR_H = 3.9;
const END_M = 0.24, AISLE_M = 0.44;
const FIELD_N = 0.012, LAMP_Y = 3.86;
const SIZE_NAME = { XS: 'Extra small', S: 'Small', M: 'Medium', L: 'Large', XL: 'Extra large' };
const TRIM = { XS: [0.02, 0.024, 0.03], S: [0.028, 0.03, 0.035], M: [0.032, 0.034, 0.04], L: [0.05, 0.045, 0.05], XL: [0, 0.06, 0.06] }; // mat, frame, depth
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

const canvas = $('#c');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Q.dpr);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.2;
const MAX_ANISO = renderer.capabilities.getMaxAnisotropy();
const scene = new THREE.Scene();
const FOG_COLOR = new THREE.Color(0x0b0705);
scene.background = FOG_COLOR;
const FOG_DENSITY = 0.04;
scene.fog = new THREE.FogExp2(FOG_COLOR, FOG_DENSITY);
const camera = new THREE.PerspectiveCamera(IS_TOUCH ? 70 : 62, 1, 0.05, 140);
scene.add(new THREE.HemisphereLight(0x9a7550, 0x1c1009, 0.32));

/* ------------------------------------------------------------------ small utils */
function rng(seed) { let a = seed >>> 0; return () => { a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
function hash(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return (h >>> 0) / 4294967296; }
const R = rng(20210101);
const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const lin = (hex) => { const c = new THREE.Color(hex); return [c.r, c.g, c.b]; };
const nextFrame = () => new Promise((r) => setTimeout(r, 0)); // yields to the page without waiting on a paint
const fmt = (n) => n.toLocaleString('en-US');
function setStatus(text, frac) { $('#status').textContent = text; if (frac != null) $('#bar').style.width = Math.round(frac * 100) + '%'; }

/* ------------------------------------------------------------------ procedural textures */
function canvasTex(cv, { srgb = true, aniso = true } = {}) {
  const t = new THREE.CanvasTexture(cv);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  if (aniso) t.anisotropy = Math.min(8, MAX_ANISO);
  return t;
}
function flutedCanvas(src) {
  const cv = document.createElement('canvas'); cv.width = 256; cv.height = 256;
  const c = cv.getContext('2d'); c.drawImage(src, 0, 0, 256, 256);
  const flutes = 5, fw = 256 / flutes;
  for (let i = 0; i < flutes; i++) {
    const g = c.createLinearGradient(i * fw, 0, (i + 1) * fw, 0);
    g.addColorStop(0, 'rgba(0,0,0,.55)'); g.addColorStop(0.18, 'rgba(0,0,0,.05)'); g.addColorStop(0.5, 'rgba(255,220,170,.12)'); g.addColorStop(0.82, 'rgba(0,0,0,.25)'); g.addColorStop(1, 'rgba(0,0,0,.6)');
    c.fillStyle = g; c.fillRect(i * fw, 0, fw, 256);
  }
  return cv;
}
function booksCanvas(seed) {
  const W = 1024, H = 320, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
  const c = cv.getContext('2d'), r = rng(seed);
  c.fillStyle = '#120a06'; c.fillRect(0, 0, W, H);
  const cloth = ['#5e1a14', '#2a3a26', '#1d2740', '#6b4a22', '#3b1d2e', '#1a1512', '#7a5a2e', '#4a2316', '#2c3b3a', '#5a4630', '#8a2a1e'];
  let x = 0;
  while (x < W) {
    const w = 16 + r() * 38, h = H * (0.62 + r() * 0.34), col = cloth[Math.floor(r() * cloth.length)];
    if (r() < 0.05) { x += 6 + r() * 20; continue; }
    const y = H - h;
    const g = c.createLinearGradient(x, 0, x + w, 0);
    g.addColorStop(0, 'rgba(0,0,0,.55)'); g.addColorStop(0.25, 'rgba(255,255,255,.06)'); g.addColorStop(0.7, 'rgba(0,0,0,.05)'); g.addColorStop(1, 'rgba(0,0,0,.6)');
    c.fillStyle = col; c.fillRect(x, y, w - 1.5, h);
    c.fillStyle = g; c.fillRect(x, y, w - 1.5, h);
    c.fillStyle = 'rgba(214,178,104,.85)';
    const bands = r() < 0.7 ? 2 : 0;
    for (let b = 0; b < bands; b++) { const by = y + h * (b ? 0.86 : 0.08); c.fillRect(x + 2, by, w - 5.5, 2.5); c.fillRect(x + 2, by + 5, w - 5.5, 1.2); }
    if (r() < 0.75) { c.fillRect(x + w * 0.25, y + h * 0.22, w * 0.45, 3); c.fillRect(x + w * 0.3, y + h * 0.3, w * 0.35, 2); }
    if (r() < 0.25) { c.fillStyle = 'rgba(0,0,0,.35)'; c.fillRect(x + 3, y + h * 0.45, w - 8, h * 0.12); }
    x += w;
  }
  const sh = c.createLinearGradient(0, 0, 0, H); sh.addColorStop(0, 'rgba(0,0,0,.7)'); sh.addColorStop(0.25, 'rgba(0,0,0,0)'); c.fillStyle = sh; c.fillRect(0, 0, W, H);
  return cv;
}
function rugCanvas(seed, W = 512, H = 1024) {
  const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
  const c = cv.getContext('2d'), r = rng(seed);
  const red = '#5c1712', deep = '#3a0e0b', navy = '#1b2140', gold = '#b8893d', cream = '#d9c7a0', teal = '#1f3b3a';
  c.fillStyle = navy; c.fillRect(0, 0, W, H);
  const b = W * 0.1;
  c.fillStyle = gold; c.fillRect(b * 0.35, b * 0.35, W - b * 0.7, H - b * 0.7);
  c.fillStyle = navy; c.fillRect(b * 0.45, b * 0.45, W - b * 0.9, H - b * 0.9);
  // border vine
  c.fillStyle = cream;
  for (let y = b * 0.6; y < H - b * 0.6; y += 22) { c.beginPath(); c.arc(b * 0.72, y, 3, 0, 7); c.arc(W - b * 0.72, y, 3, 0, 7); c.fill(); }
  for (let x = b * 0.6; x < W - b * 0.6; x += 22) { c.beginPath(); c.arc(x, b * 0.72, 3, 0, 7); c.arc(x, H - b * 0.72, 3, 0, 7); c.fill(); }
  c.fillStyle = gold; c.fillRect(b, b, W - 2 * b, H - 2 * b);
  c.fillStyle = red; c.fillRect(b * 1.12, b * 1.12, W - 2.24 * b, H - 2.24 * b);
  // lozenge lattice
  c.save(); c.beginPath(); c.rect(b * 1.12, b * 1.12, W - 2.24 * b, H - 2.24 * b); c.clip();
  const s = 46;
  for (let y = 0; y < H + s; y += s) for (let x = ((y / s) % 2) * s / 2; x < W + s; x += s) {
    c.fillStyle = deep; c.beginPath(); c.moveTo(x, y - s * 0.42); c.lineTo(x + s * 0.3, y); c.lineTo(x, y + s * 0.42); c.lineTo(x - s * 0.3, y); c.fill();
    c.fillStyle = gold; c.fillRect(x - 2, y - 2, 4, 4);
  }
  // medallions
  const meds = Math.max(1, Math.round(H / W));
  for (let m = 0; m < meds; m++) {
    const cx = W / 2, cy = H * (m + 0.5) / meds, rad = W * 0.27;
    const layers = [[1, navy], [0.84, gold], [0.74, teal], [0.56, cream], [0.44, red], [0.26, gold], [0.12, navy]];
    for (const [k, col] of layers) { c.fillStyle = col; c.beginPath(); c.moveTo(cx, cy - rad * k * 1.25); c.lineTo(cx + rad * k, cy); c.lineTo(cx, cy + rad * k * 1.25); c.lineTo(cx - rad * k, cy); c.fill(); }
  }
  c.restore();
  // wool speckle and wear
  const img = c.getImageData(0, 0, W, H), D = img.data;
  for (let i = 0; i < D.length; i += 4) { const n = (r() - 0.5) * 26; D[i] += n; D[i + 1] += n; D[i + 2] += n; }
  c.putImageData(img, 0, 0);
  return cv;
}
function tuftNormalCanvas() {
  const S = 256, cv = document.createElement('canvas'); cv.width = cv.height = S;
  const c = cv.getContext('2d'), img = c.createImageData(S, S), D = img.data;
  const hgt = new Float32Array(S * S), cell = S / 4;
  for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
    // diamond lattice: buttons at cell centres of a 45-degree grid
    const u = (x + y) / cell, v = (x - y) / cell;
    const fu = u - Math.round(u), fv = v - Math.round(v);
    const d = Math.sqrt(fu * fu + fv * fv);
    const crease = Math.min(Math.abs(fu), Math.abs(fv));
    hgt[y * S + x] = Math.min(1, d * 1.9) * 0.8 + smooth(0, 0.12, crease) * 0.2;
  }
  for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
    const hx = hgt[y * S + ((x + 1) % S)] - hgt[y * S + ((x - 1 + S) % S)];
    const hy = hgt[((y + 1) % S) * S + x] - hgt[((y - 1 + S) % S) * S + x];
    const nx = -hx * 3, ny = -hy * 3, nz = 1, l = Math.hypot(nx, ny, nz), i = (y * S + x) * 4;
    D[i] = (nx / l * 0.5 + 0.5) * 255; D[i + 1] = (ny / l * 0.5 + 0.5) * 255; D[i + 2] = (nz / l * 0.5 + 0.5) * 255; D[i + 3] = 255;
  }
  c.putImageData(img, 0, 0);
  return cv;
}
function radialCanvas(size, stops, sx = 1, sy = 1, cy = 0.5) {
  const cv = document.createElement('canvas'); cv.width = size * sx; cv.height = size * sy;
  const c = cv.getContext('2d');
  c.setTransform(sx, 0, 0, sy, 0, 0);
  const g = c.createRadialGradient(size / 2, size * cy, 0, size / 2, size * cy, size / 2);
  for (const [o, col] of stops) g.addColorStop(o, col);
  c.fillStyle = g; c.fillRect(0, 0, size, size);
  return cv;
}
function flameCanvas() {
  const cv = document.createElement('canvas'); cv.width = 64; cv.height = 64;
  const c = cv.getContext('2d');
  c.translate(32, 40); c.scale(0.42, 1);
  const g = c.createRadialGradient(0, 0, 0, 0, -4, 26);
  g.addColorStop(0, 'rgba(255,255,235,1)'); g.addColorStop(0.35, 'rgba(255,214,120,.95)'); g.addColorStop(0.7, 'rgba(255,120,30,.45)'); g.addColorStop(1, 'rgba(255,80,0,0)');
  c.fillStyle = g; c.beginPath(); c.arc(0, 0, 26, 0, 7); c.fill();
  return cv;
}
function shadowCanvas() {
  const cv = document.createElement('canvas'); cv.width = cv.height = 64;
  const c = cv.getContext('2d');
  c.shadowColor = 'rgba(0,0,0,1)'; c.shadowBlur = 9; c.shadowOffsetX = 200;
  c.fillStyle = '#000'; c.fillRect(14 - 200, 14, 36, 36);
  return cv;
}

/* ------------------------------------------------------------------ geometry buffer */
class Buf {
  constructor(cap = 2048, uv1 = false) { this.n = 0; this.cap = cap; this.p = new Float32Array(cap * 3); this.no = new Float32Array(cap * 3); this.uv = new Float32Array(cap * 2); this.c = new Float32Array(cap * 3); this.u1 = uv1 ? new Float32Array(cap * 2) : null; }
  grow(k) {
    if (this.n + k <= this.cap) return;
    let cap = this.cap; while (cap < this.n + k) cap *= 2;
    const g = (a, s) => { const b = new Float32Array(cap * s); b.set(a); return b; };
    this.p = g(this.p, 3); this.no = g(this.no, 3); this.uv = g(this.uv, 2); this.c = g(this.c, 3); if (this.u1) this.u1 = g(this.u1, 2);
    this.cap = cap;
  }
  v(x, y, z, nx, ny, nz, u, v, r, gg, b, u1 = 0, v1 = 0) {
    const i = this.n++;
    this.p[i * 3] = x; this.p[i * 3 + 1] = y; this.p[i * 3 + 2] = z;
    this.no[i * 3] = nx; this.no[i * 3 + 1] = ny; this.no[i * 3 + 2] = nz;
    this.uv[i * 2] = u; this.uv[i * 2 + 1] = v;
    this.c[i * 3] = r; this.c[i * 3 + 1] = gg; this.c[i * 3 + 2] = b;
    if (this.u1) { this.u1[i * 2] = u1; this.u1[i * 2 + 1] = v1; }
  }
  /** Quad a-b-c-d (Vector3s); winding is fixed up to face `nrm`. uvs: [[u,v]x4], cols: [r,g,b] or 4 of them. */
  quad(a, b, c, d, nrm, uvs, cols, uv1s) {
    this.grow(6);
    const e1x = b.x - a.x, e1y = b.y - a.y, e1z = b.z - a.z, e2x = c.x - a.x, e2y = c.y - a.y, e2z = c.z - a.z;
    const cx = e1y * e2z - e1z * e2y, cy = e1z * e2x - e1x * e2z, cz = e1x * e2y - e1y * e2x;
    let order = [0, 1, 2, 0, 2, 3];
    if (cx * nrm.x + cy * nrm.y + cz * nrm.z < 0) order = [0, 2, 1, 0, 3, 2];
    const P = [a, b, c, d];
    for (const k of order) {
      const p = P[k], uv = uvs ? uvs[k] : [0, 0], col = cols ? (Array.isArray(cols[0]) ? cols[k] : cols) : [1, 1, 1], u1 = uv1s ? uv1s[k] : [0, 0];
      this.v(p.x, p.y, p.z, nrm.x, nrm.y, nrm.z, uv[0], uv[1], col[0], col[1], col[2], u1[0], u1[1]);
    }
  }
  /** Axis-aligned box with wood-style UVs: grain runs along `grain` axis. */
  box(cx, cy, cz, sx, sy, sz, o = {}) {
    this.grow(36);
    const hx = sx / 2, hy = sy / 2, hz = sz / 2;
    const grain = o.grain || (sx >= sy && sx >= sz ? 'x' : sy >= sz ? 'y' : 'z');
    const [sa, sl] = o.scale || [0.9, 2.4];
    const ou = o.ou ?? R() * 7, ov = o.ov ?? R() * 7;
    const col = o.color || [1, 1, 1];
    const faces = [
      ['x', 1, [[hx, -hy, hz], [hx, -hy, -hz], [hx, hy, -hz], [hx, hy, hz]]],
      ['x', -1, [[-hx, -hy, -hz], [-hx, -hy, hz], [-hx, hy, hz], [-hx, hy, -hz]]],
      ['y', 1, [[-hx, hy, hz], [hx, hy, hz], [hx, hy, -hz], [-hx, hy, -hz]]],
      ['y', -1, [[-hx, -hy, -hz], [hx, -hy, -hz], [hx, -hy, hz], [-hx, -hy, hz]]],
      ['z', 1, [[-hx, -hy, hz], [hx, -hy, hz], [hx, hy, hz], [-hx, hy, hz]]],
      ['z', -1, [[hx, -hy, -hz], [-hx, -hy, -hz], [-hx, hy, -hz], [hx, hy, -hz]]],
    ];
    const AX = { x: 0, y: 1, z: 2 };
    for (const [ax, sg, cs] of faces) {
      if (o.skip && o.skip.includes((sg > 0 ? '+' : '-') + ax)) continue;
      const inPlane = ['x', 'y', 'z'].filter((a) => a !== ax);
      const along = inPlane.includes(grain) ? grain : inPlane[0];
      const across = inPlane.find((a) => a !== along);
      const nx = ax === 'x' ? sg : 0, ny = ax === 'y' ? sg : 0, nz = ax === 'z' ? sg : 0;
      const uv = (p) => [p[AX[across]] / sa + ou, p[AX[along]] / sl + ov];
      for (const k of [0, 1, 2, 0, 2, 3]) {
        const p = cs[k], t = uv(p);
        this.v(cx + p[0], cy + p[1], cz + p[2], nx, ny, nz, t[0], t[1], col[0], col[1], col[2]);
      }
    }
  }
  addGeometry(geo, m, col = [1, 1, 1], uvScale = 1) {
    const g = geo.index ? geo.toNonIndexed() : geo;
    const P = g.attributes.position, N = g.attributes.normal, U = g.attributes.uv;
    const nm = new THREE.Matrix3().getNormalMatrix(m), v = new THREE.Vector3(), n = new THREE.Vector3();
    this.grow(P.count);
    for (let i = 0; i < P.count; i++) {
      v.fromBufferAttribute(P, i).applyMatrix4(m); n.fromBufferAttribute(N, i).applyMatrix3(nm).normalize();
      this.v(v.x, v.y, v.z, n.x, n.y, n.z, U ? U.getX(i) * uvScale : 0, U ? U.getY(i) * uvScale : 0, col[0], col[1], col[2]);
    }
  }
  geometry() {
    const g = new THREE.BufferGeometry(), n = this.n;
    g.setAttribute('position', new THREE.BufferAttribute(this.p.slice(0, n * 3), 3));
    g.setAttribute('normal', new THREE.BufferAttribute(this.no.slice(0, n * 3), 3));
    g.setAttribute('uv', new THREE.BufferAttribute(this.uv.slice(0, n * 2), 2));
    g.setAttribute('color', new THREE.BufferAttribute(this.c.slice(0, n * 3), 3));
    if (this.u1) g.setAttribute('uv1', new THREE.BufferAttribute(this.u1.slice(0, n * 2), 2));
    g.computeBoundingSphere(); g.computeBoundingBox();
    return g;
  }
}

/* ------------------------------------------------------------------ materials */
const T = {}, M = {};
async function buildMaterials(progress) {
  // Wood is generated offline (tools/make_wood.py) and shipped as small tileable images.
  const names = ['walnut', 'field', 'mahog', 'burl', 'ceil', 'floor'];
  let done = 0;
  await Promise.all(names.map((k) => new Promise((res, rej) => loader.load(ASSET(`tex/${k}.webp`), (t) => {
    t.wrapS = t.wrapT = THREE.RepeatWrapping; t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = Math.min(8, MAX_ANISO);
    T[k] = t; progress(++done / (names.length + 3)); res();
  }, undefined, () => rej(new Error('missing texture ' + k))))));
  T.fluted = canvasTex(flutedCanvas(T.mahog.image));
  T.books = [canvasTex(booksCanvas(3)), canvasTex(booksCanvas(9)), canvasTex(booksCanvas(17))];
  T.rug = canvasTex(rugCanvas(5, 512, 1536)); T.rugWide = canvasTex(rugCanvas(8, 768, 1024));
  for (const t of [T.rug, T.rugWide]) { t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; }
  T.tuft = canvasTex(tuftNormalCanvas(), { srgb: false });
  T.halo = canvasTex(radialCanvas(128, [[0, 'rgba(255,214,150,1)'], [0.18, 'rgba(255,170,80,.55)'], [0.5, 'rgba(255,120,40,.12)'], [1, 'rgba(255,100,20,0)']]), { aniso: false });
  T.flame = canvasTex(flameCanvas(), { aniso: false });
  T.scallop = canvasTex(radialCanvas(128, [[0, 'rgba(255,255,255,1)'], [0.35, 'rgba(255,255,255,.45)'], [1, 'rgba(255,255,255,0)']], 1, 2, 0.02), { aniso: false });
  T.contact = canvasTex(radialCanvas(128, [[0, 'rgba(0,0,0,.75)'], [0.6, 'rgba(0,0,0,.35)'], [1, 'rgba(0,0,0,0)']]), { aniso: false });
  T.shadow = canvasTex(shadowCanvas(), { aniso: false });
  for (const t of [T.halo, T.flame, T.scallop, T.contact, T.shadow]) { t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; }
  progress((names.length + 2) / (names.length + 3)); await nextFrame();

  const env = makeEnvironment();
  const std = (o) => new THREE.MeshStandardMaterial({ vertexColors: true, envMap: env, envMapIntensity: 0.3, ...o });
  M.walnut = std({ map: T.walnut, roughness: 0.5 });
  M.field = std({ map: T.field, roughness: 0.48 });
  M.mahog = std({ map: T.mahog, color: 0xcdb6a6, roughness: 0.42, envMapIntensity: 0.55 });
  M.burl = std({ map: T.burl, roughness: 0.38, envMapIntensity: 0.75 });
  M.fluted = std({ map: T.fluted, color: 0xcdb6a6, roughness: 0.45 });
  M.ceil = std({ map: T.ceil, roughness: 0.7, envMapIntensity: 0.2 });
  M.floor = std({ map: T.floor, roughness: 0.36, envMapIntensity: 0.45 });
  M.brass = std({ color: 0xd2a85c, metalness: 1, roughness: 0.3, envMapIntensity: 1.25 });
  M.iron = std({ color: 0x1b1714, metalness: 0.4, roughness: 0.75 });
  M.wax = std({ color: 0xf1e6cc, roughness: 0.55, emissive: 0x4a2a10, emissiveIntensity: 0.6 });
  M.leather = std({ color: 0x6a2219, roughness: 0.46, normalMap: T.tuft, normalScale: new THREE.Vector2(0.8, 0.8), envMapIntensity: 0.6 });
  M.felt = std({ color: 0x23402c, roughness: 0.85, envMapIntensity: 0.1 });
  M.cloth = std({ roughness: 0.8, envMapIntensity: 0.1 });
  M.paper = std({ color: 0xe9dfc8, roughness: 0.9, envMapIntensity: 0.05 });
  M.books = T.books.map((t) => std({ map: t, roughness: 0.8, envMapIntensity: 0.15 }));
  M.rug = std({ map: T.rug, roughness: 1, envMapIntensity: 0 });
  M.rugWide = std({ map: T.rugWide, roughness: 1, envMapIntensity: 0 });
  M.frameDark = std({ roughness: 0.4, envMapIntensity: 0.7 });
  M.frameGilt = std({ metalness: 0.85, roughness: 0.33, envMapIntensity: 1.2 });
  M.mat = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
  M.artPending = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false, color: 0xb59466 });
  M.shadow = new THREE.MeshBasicMaterial({ map: T.shadow, color: 0x000000, transparent: true, opacity: 0.55, depthWrite: false });
  M.contact = new THREE.MeshBasicMaterial({ map: T.contact, transparent: true, depthWrite: false });
  M.scallop = new THREE.MeshBasicMaterial({ map: T.scallop, color: new THREE.Color(0.55, 0.36, 0.18), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false });
  M.ember = new THREE.MeshBasicMaterial({ map: T.halo, color: new THREE.Color(1.0, 0.45, 0.12), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false });
  M.glowHalo = glowMaterial(T.halo); M.glowFlame = glowMaterial(T.flame);
  progress(1);
}
function makeEnvironment() {
  const s = new THREE.Scene();
  s.add(new THREE.Mesh(new THREE.BoxGeometry(14, 7, 40), new THREE.MeshBasicMaterial({ color: 0x2a190e, side: THREE.BackSide })));
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(14, 40), new THREE.MeshBasicMaterial({ color: 0x120a05 })); floor.rotation.x = -Math.PI / 2; floor.position.y = -3.4; s.add(floor);
  const warm = new THREE.MeshBasicMaterial({ color: new THREE.Color(9, 5.2, 2.2) });
  for (const z of [-14, -5, 4, 13]) { const b = new THREE.Mesh(new THREE.SphereGeometry(0.7, 12, 8), warm); b.position.set(0, 2.2, z); s.add(b); }
  const side = new THREE.MeshBasicMaterial({ color: new THREE.Color(1.6, 0.9, 0.4) });
  for (const x of [-6.8, 6.8]) { const p = new THREE.Mesh(new THREE.PlaneGeometry(30, 1.6), side); p.position.set(x, 0.5, 0); p.rotation.y = x > 0 ? -Math.PI / 2 : Math.PI / 2; s.add(p); }
  const pm = new THREE.PMREMGenerator(renderer);
  const t = pm.fromScene(s, 0.035).texture; pm.dispose();
  return t;
}
function glowMaterial(tex) {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 }, uTex: { value: tex }, uScale: { value: 600 }, uFog: { value: FOG_DENSITY } },
    vertexShader: `attribute float aSize; attribute float aPhase; uniform float uTime; uniform float uScale; uniform float uFog; varying float vA;
      void main(){ vec4 mv = modelViewMatrix * vec4(position, 1.0);
        float f = 0.86 + 0.09 * sin(uTime * 9.0 + aPhase * 6.3) + 0.05 * sin(uTime * 23.0 + aPhase * 2.1);
        float d = -mv.z; vA = f * exp(-pow(d * uFog, 2.0));
        gl_PointSize = clamp(aSize * f * uScale / max(d, 0.05), 1.0, 256.0);
        gl_Position = projectionMatrix * mv; }`,
    fragmentShader: `uniform sampler2D uTex; varying float vA; void main(){ vec4 c = texture2D(uTex, gl_PointCoord); gl_FragColor = vec4(c.rgb * c.a * vA, 1.0); }`,
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
  });
}

/* ------------------------------------------------------------------ plaques (canvas text on brass) */
function plaque(w, h, draw, kind = 'brass') {
  const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  const c = cv.getContext('2d');
  if (kind === 'brass') {
    const g = c.createLinearGradient(0, 0, w * 0.3, h); g.addColorStop(0, '#dcb874'); g.addColorStop(0.5, '#b88f4b'); g.addColorStop(1, '#8c6630');
    c.fillStyle = g; c.fillRect(0, 0, w, h);
    for (let y = 0; y < h; y += 2) { c.fillStyle = `rgba(255,236,190,${Math.random() * 0.06})`; c.fillRect(0, y, w, 1); }
    c.strokeStyle = 'rgba(70,45,15,.85)'; c.lineWidth = Math.max(3, h * 0.025); c.strokeRect(c.lineWidth * 1.5, c.lineWidth * 1.5, w - c.lineWidth * 3, h - c.lineWidth * 3);
  } else if (kind === 'felt') {
    c.fillStyle = '#141110'; c.fillRect(0, 0, w, h);
    c.fillStyle = 'rgba(255,255,255,.035)'; for (let y = 0; y < h; y += 8) c.fillRect(0, y, w, 3);
  }
  draw(c, w, h);
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = Math.min(8, MAX_ANISO);
  return t;
}
function engrave(c, text, x, y, font, align = 'center', color = '#2b1a0b') {
  c.font = font; c.textAlign = align; c.textBaseline = 'middle';
  c.fillStyle = 'rgba(255,238,196,.55)'; c.fillText(text, x, y + 2);
  c.fillStyle = color; c.fillText(text, x, y);
}
function plaqueMesh(tex, w, h, kind = 'brass') {
  const mat = kind === 'brass'
    ? new THREE.MeshStandardMaterial({ map: tex, metalness: 0.55, roughness: 0.42, envMap: M.brass.envMap, envMapIntensity: 0.7 })
    : new THREE.MeshStandardMaterial({ map: tex, roughness: 0.9, envMapIntensity: 0.1 });
  return new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
}
function wrapLines(c, text, maxW) {
  const words = text.split(' '), lines = []; let line = '';
  for (const w of words) { const t = line ? line + ' ' + w : w; if (c.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t; }
  if (line) lines.push(line); return lines;
}

/* ------------------------------------------------------------------ walls (local frames) */
function makeWall(sx, sz, nx, nz, len, m0 = END_M, m1 = END_M, extra = {}) {
  return { sx, sz, N: [nx, nz], U: [nz, -nx], len, m0, m1, pieces: [], lamps: [], lampY: LAMP_Y, ...extra };
}
function wallPoint(w, u, y, n, out = new THREE.Vector3()) { return out.set(w.sx + w.U[0] * u + w.N[0] * n, y, w.sz + w.U[1] * u + w.N[1] * n); }
function wallPointW(w, u, y, n, out = new THREE.Vector3()) { const W = w.W; return out.set(W.sx + W.U[0] * u + W.N[0] * n, y, W.sz + W.U[1] * u + W.N[1] * n); }
function wbox(buf, w, u0, u1, y0, y1, n0, n1, o = {}) {
  const c = wallPoint(w, (u0 + u1) / 2, (y0 + y1) / 2, (n0 + n1) / 2);
  const du = Math.abs(u1 - u0), dn = Math.abs(n1 - n0), alongX = Math.abs(w.U[0]) > 0.5;
  let grain = o.grain;
  if (grain === 'u') grain = alongX ? 'x' : 'z';
  buf.box(c.x, c.y, c.z, alongX ? du : dn, y1 - y0, alongX ? dn : du, { ...o, grain });
}

/* ------------------------------------------------------------------ hanging */
// Each room hangs to a style: the band art may occupy, where columns centre, picture-lamp height, frame trims.
const STYLE = {
  salon: { band: [0.7, 3.35], yc: 1.85, gapV: 0.04, gapH: 0.045, lampY: 3.62, light: 0.5,
    trim: { XS: [0.012, 0.016, 0.024], S: [0.016, 0.02, 0.028], M: [0.02, 0.024, 0.032], L: [0.028, 0.034, 0.04], XL: [0, 0.045, 0.05] } },
  long: { band: [0.84, 2.16], yc: 1.5, rows: 4, slot: 0.318, gapV: 0.04, gapH: 0.046, lampY: 2.52, light: 0.4, trim: { XS: [0.012, 0.018, 0.025] } },
  gallery: { band: [0.78, 2.42], yc: 1.55, gapV: 0.05, gapH: 0.07, lampY: 2.85, light: 0.5, trim: TRIM },
  grand: { band: [0.7, 3.3], yc: 1.65, gapV: 0.06, gapH: 0.16, lampY: 3.62, light: 0.5, trim: TRIM },
};
function applyTrim(p, st) {
  const t = st.trim[p.size] || TRIM[p.size] || TRIM.XS;
  p.mat = t[0]; p.fr = t[1]; p.depth = t[2]; p.ow = p.w + 2 * (t[0] + t[1]); p.oh = p.h + 2 * (t[0] + t[1]);
}
function hangWall(wall, list, q) {
  const st = wall.style || STYLE.gallery, [b0, b1] = wall.band || st.band, band = b1 - b0;
  wall.lampY = wall.lampYAt ?? st.lampY; wall.light = st.light;
  const avail = wall.len - wall.m0 - wall.m1;
  const cols = []; let used = 0, j = q;
  while (j < list.length) {
    const col = []; let cw = 0, ch = 0, k = j;
    while (k < list.length) {
      const p = list[k];
      if (st.rows ? col.length >= st.rows : col.length && (ch + st.gapV + p.oh > band || p.ow > cw * 1.7 || p.ow * 1.7 < cw)) break;
      ch += (col.length ? st.gapV : 0) + p.oh; col.push(p); cw = Math.max(cw, p.ow); k++;
      if (!st.rows && ch >= band) break;
    }
    const need = (cols.length ? st.gapH : 0) + cw;
    if (used + need > avail && cols.length) break;
    cols.push({ col, cw, ch }); used += need; j = k;
  }
  const finished = j >= list.length;
  let gap = st.gapH;
  if (cols.length > 1 && !finished) gap = Math.min(st.gapH + (avail - used) / (cols.length - 1), st.rows ? 0.14 : 0.42);
  const total = cols.reduce((s, c) => s + c.cw, 0) + gap * Math.max(0, cols.length - 1);
  let u = wall.m0 + Math.max(0, (avail - total) / 2);
  for (const c of cols) {
    const uc = u + c.cw / 2;
    if (st.rows) {
      // neat rows: every column has the same slots, each drawing centred in its slot
      c.col.forEach((p, r) => { p.wall = wall; p.u = uc; p.y = st.yc + ((st.rows - 1) / 2 - r) * st.slot; wall.pieces.push(p); });
    } else {
      const yc = Math.min(b1 - c.ch / 2, Math.max(b0 + c.ch / 2, wall.yc ?? st.yc));
      let top = yc + c.ch / 2;
      for (const p of c.col) { p.wall = wall; p.u = uc; p.y = top - p.oh / 2; top -= p.oh + st.gapV; wall.pieces.push(p); }
    }
    u += c.cw + gap;
  }
  if (!wall.lamps.length) { const n = Math.max(1, Math.round(wall.len / 1.45)); for (let i = 0; i < n; i++) wall.lamps.push((i + 0.5) * wall.len / n); }
  return j;
}
function alcoveWalls(side, zt, zb, st) {
  const ws = side < 0
    ? [makeWall(-HALF, zt, 0, -1, ALC_D, AISLE_M, END_M), makeWall(-OUT_X, zt, 1, 0, ALC_W), makeWall(-OUT_X, zb, 0, 1, ALC_D, END_M, AISLE_M)]
    : [makeWall(HALF, zb, 0, 1, ALC_D, AISLE_M, END_M), makeWall(OUT_X, zb, -1, 0, ALC_W), makeWall(OUT_X, zt, 0, -1, ALC_D, END_M, AISLE_M)];
  for (const w of ws) w.style = st;
  return ws;
}
function lampLight(w, u, y) {
  let s = 0; const ly = w.lampY ?? LAMP_Y;
  for (const lu of w.lamps) { const du = u - lu, dy = ly - y; s += Math.exp(-(du * du) / 1.1) / (0.7 + dy * dy * 0.12); }
  return Math.min(1.0, 0.36 + s * (w.light ?? 0.5));
}

/* ------------------------------------------------------------------ rooms in the world */
// Rooms are planned and built in their own local frame (aisle toward -Z), then placed with a
// quarter-turn rotation and an offset. World copies of walls, colliders and lights drive picking,
// collisions and the light pool.
function setTf(room, rot, ox, oz) { room.tf = { rot, c: Math.round(Math.cos(rot)), s: Math.round(Math.sin(rot)), ox, oz }; return room; }
function toW(room, x, z) { const t = room.tf; return [x * t.c + z * t.s + t.ox, -x * t.s + z * t.c + t.oz]; }
function dirW(room, x, z) { const t = room.tf; return [x * t.c + z * t.s, -x * t.s + z * t.c]; }
function boxW(room, b) { const a = toW(room, b[0], b[2]), c = toW(room, b[1], b[3]); return [Math.min(a[0], c[0]), Math.max(a[0], c[0]), Math.min(a[1], c[1]), Math.max(a[1], c[1])]; }
function finalizeRoom(room) {
  room.boundsW = boxW(room, room.bounds);
  room.collW = room.colliders.map((b) => boxW(room, b));
  room.lightsW = room.lights.map((l) => { const [x, z] = toW(room, l.pos.x, l.pos.z); return { ...l, pos: new THREE.Vector3(x, l.pos.y, z) }; });
  for (const w of room.walls) { const [sx, sz] = toW(room, w.sx, w.sz); w.W = { sx, sz, N: dirW(room, w.N[0], w.N[1]), U: dirW(room, w.U[0], w.U[1]) }; }
  if (room.fireL) { const [x, z] = toW(room, room.fireL.x, room.fireL.z); room.fire = new THREE.Vector3(x, room.fireL.y, z); }
}
function spawnOf(room, local = room.spawnL) {
  const [x, z] = toW(room, local.x, local.z);
  return { x, z, yaw: (local.yaw || 0) + room.tf.rot };
}

/* ------------------------------------------------------------------ world plan */
const world = { pieces: [], byN: new Map(), rooms: [], chunks: [], loAtlases: [], hiFiles: [], atlas: 2048, labels: null };
const SWH = 8.5, FIRE_U = 6.2;                                   // salon half-width; fireplace centre from the back wall
const LH = 3.2, LW_T = 0.3, BAY = 5.4, PIER = 0.5, L_ENTRY = 2.6, L_END = 3.4, L_CEIL = 4.6; // the Long Gallery
const VEST_W = 1.5, VEST_L = 2.4, VEST_CEIL = 4.3;               // passages between rooms
const ERA = { 2016: 'Early career', 2019: '2018–2020', 2020: 'Water series (retired)' };
const eraOf = (p) => (p.early ? ERA[p.year] || `${p.year}, unnumbered` : String(p.year));
const stampOf = (p) => (p.early ? 'Unnumbered' : `No. ${p.n}`);

function rangeOf(list) { return list.length ? [list[0].n, list[list.length - 1].n] : ['', '']; }
function planSalon(list, backDoor = true) {
  const st = STYLE.salon;
  list.forEach((p) => applyTrim(p, st));
  for (let len = 22; len < 90; len++) {
    const W = SWH, uc = FIRE_U;
    const mk = (sx, sz, nx, nz, l, extra = {}) => ({ ...makeWall(sx, sz, nx, nz, l, 0.3, 0.3), style: st, ...extra });
    // Hanging order: over the mantel first, then clockwise round the room, ending beside the fire.
    const walls = [
      mk(-W + 0.01, -(uc - 1.22), 1, 0, 2.44, { band: [1.62, 3.35], m0: 0.05, m1: 0.05, mantel: true }), // sits on the burl overmantel
      mk(-W, -(uc + 1.45), 1, 0, len - (uc + 1.45)),
      mk(-W, -len, 0, 1, W - 1.55),
      mk(1.55, -len, 0, 1, W - 1.55),
      mk(W, -len, -1, 0, len),
      mk(W, 0, 0, -1, W - 4.45),
      mk(-4.45, 0, 0, -1, W - 4.45),
      mk(-W, 0, 1, 0, uc - 1.45),
    ];
    let q = 0;
    for (const w of walls) q = hangWall(w, list, q);
    if (q < list.length) continue;
    const [a, b] = rangeOf(list);
    return {
      kind: 'salon', key: 'first', title: 'The First Thousand', name: 'The First Thousand', len, walls, list, ceil: CEIL, backDoor,
      sub: `Nos. ${a} – ${b} · fireside`, range: `Nos. ${a} – ${b}`, rangeShort: `${a}–${b}`,
      bounds: [-W, W, -len, 0], spawnL: { x: -1.6, z: -2.2, yaw: 0.78 }, // facing the fire and the first drawings
      colliders: [
        ...(backDoor ? [[-W - 1, -1.2, 0, 1], [1.2, W + 1, 0, 1]] : [[-W - 1, W + 1, 0, 1]]), [-W - 1, -1.2, -len - 1, -len], [1.2, W + 1, -len - 1, -len],
        [-W - 1, -W + 0.12, -len - 1, 1], [W - 0.12, W + 1, -len - 1, 1], [-W, -W + 0.85, -uc - 1.35, -uc + 1.35],
      ],
      lights: [],
    };
  }
  throw new Error('the first thousand did not fit');
}
function planVestibule(name) {
  return {
    kind: 'vest', name, title: name, sub: '', ceil: VEST_CEIL, walls: [], list: [], lights: [],
    bounds: [-VEST_W, VEST_W, -VEST_L, 0], spawnL: { x: 0, z: -VEST_L / 2, yaw: 0 },
    colliders: [[-VEST_W - 0.4, -VEST_W + 0.05, -VEST_L - 0.2, 0.2], [VEST_W - 0.05, VEST_W + 0.4, -VEST_L - 0.2, 0.2]],
  };
}
function planLong(list) {
  const st = STYLE.long;
  list.forEach((p) => applyTrim(p, st));
  const bayTop = (b) => -(L_ENTRY + PIER + b * (BAY + PIER));
  const pass = (doorBays) => {
    const walls = []; let q = 0, k = 0;
    while (q < list.length || k % 2 === 1) {
      const bay = Math.floor(k / 2), side = k % 2 === 0 ? -1 : 1, top = bayTop(bay), bot = top - BAY;
      const w = side < 0 ? makeWall(-LH, top, 1, 0, BAY, 0.2, 0.2) : makeWall(LH, bot, -1, 0, BAY, 0.2, 0.2);
      Object.assign(w, { style: st, bay, side });
      if (side > 0 && doorBays.includes(bay)) { w.door = doorBays.indexOf(bay); w.lampY = st.lampY; }
      else q = hangWall(w, list, q);
      walls.push(w); k++;
    }
    return { walls, bays: Math.ceil(k / 2) };
  };
  let r = pass([]);
  const doorBays = [0.2, 0.5, 0.8].map((f) => Math.max(1, Math.round(r.bays * f)));
  for (const p of list) p.wall = null;
  r = pass(doorBays);
  const len = L_ENTRY + r.bays * (BAY + PIER) + PIER + L_END;
  const doors = doorBays.map((b, i) => ({ i, bay: b, z: bayTop(b) - BAY / 2 }));
  // where each year first appears along the gallery
  const years = [];
  for (const p of list) if (!years.some((y) => y.year === p.year) && (!years.length || p.year > years[years.length - 1].year)) years.push({ year: p.year, piece: p });
  for (const y of years) { y.bay = y.piece.wall.bay; y.z = bayTop(y.bay) + PIER / 2; }
  const colliders = [[-LH - 1, -LH + 0.12, -len - 1, 1], [-LH - 1, -1.2, 0, 1], [1.2, LH + 1, 0, 1], [-LH - 1, LH + 1, -len - 1, -len]];
  let zTop = 1;
  for (const d of [...doors].sort((a, b) => b.z - a.z)) { colliders.push([LH - 0.12, LH + 1, d.z + 1.2, zTop]); zTop = d.z - 1.2; }
  colliders.push([LH - 0.12, LH + 1, -len - 1, zTop]);
  const [a, b] = rangeOf(list);
  return {
    kind: 'long', key: 'long', title: 'The Long Gallery', name: 'The Long Gallery', len, walls: r.walls, list, bays: r.bays, doors, years, ceil: L_CEIL,
    sub: `Extra-small drawings · Nos. ${a} – ${b}`, range: `Nos. ${a} – ${b}`, rangeShort: `${a}–${b}`,
    bounds: [-LH, LH, -len, 0], spawnL: { x: 0, z: -1.4, yaw: 0 }, colliders, lights: [],
  };
}
function planAlcoveGallery(meta, list) {
  const st = STYLE[meta.style];
  list.forEach((p) => applyTrim(p, st));
  const room = { kind: 'gallery', ceil: CEIL, alcoves: [], walls: [], list, colliders: [], lights: [], z0: 0, zA: -PAD, ...meta };
  let q = 0, k = 0;
  while (q < list.length || k % 2 === 1) {
    const pair = Math.floor(k / 2), side = k % 2 === 0 ? -1 : 1;
    const zt = room.zA - pair * PITCH, zb = zt - ALC_W;
    const walls = alcoveWalls(side, zt, zb, st);
    const alc = { side, pair, zt, zb, walls, index: k, pieces: [] };
    const start = q;
    for (const wall of walls) { wall.alcove = alc; q = hangWall(wall, list, q); }
    alc.pieces = list.slice(start, q);
    if (q === start && q < list.length) throw new Error('hanging stalled');
    room.alcoves.push(alc); room.walls.push(...walls); k++;
  }
  room.pairs = Math.ceil(k / 2);
  room.zbLast = room.zA - (room.pairs - 1) * PITCH - ALC_W;
  room.z1 = room.zbLast - PAD; room.len = -room.z1;
  const [a, b] = rangeOf(list);
  room.range = room.early ? 'Unnumbered, before and between the stamps' : `Nos. ${a} – ${b}`;
  room.rangeShort = room.early ? 'unnumbered' : `${a}–${b}`;
  room.sub = room.early ? `Unnumbered · ${fmt(list.length)} pieces` : `${room.sizeLine} · Nos. ${a} – ${b}`;
  room.bounds = [-OUT_X, OUT_X, room.z1, 0];
  room.spawnL = { x: 0, z: -1.4, yaw: 0 };
  const box = (x0, x1, z0, z1) => room.colliders.push([Math.min(x0, x1), Math.max(x0, x1), Math.min(z0, z1), Math.max(z0, z1)]);
  const z1 = room.z1;
  box(-OUT_X - 1, -OUT_X, z1 - 1, 1); box(OUT_X, OUT_X + 1, z1 - 1, 1);
  box(-OUT_X - 1, -DOOR_W / 2, 0, WALL_T + 0.3); box(DOOR_W / 2, OUT_X + 1, 0, WALL_T + 0.3);
  box(-OUT_X - 1, OUT_X + 1, z1 - 1, z1);
  for (const s of [-1, 1]) {
    const xa = s * (HALF - 0.1), xb = s * OUT_X;
    box(xa, xb, room.zA, 0); box(xa, xb, z1, room.zbLast);
    for (let p = 0; p < room.pairs - 1; p++) { const zb = room.zA - p * PITCH - ALC_W; box(xa, xb, zb - PART_T - 0.1, zb + 0.1); }
  }
  return room;
}

function planMuseum(data, early) {
  // Early and unnumbered work keeps its own atlases under art/early/; their indices follow the numbered set.
  const hiList = data.hi.concat(early ? early.hi : []), loList = data.lo.concat(early ? early.lo : []);
  world.atlas = data.atlas; world.hiFiles = hiList; world.loAtlases = loList.map(([url, h]) => ({ url, h, tex: null, mat: null, state: 'none' }));
  const toPiece = (r, offHi, offLo, isEarly) => {
    const [n, year, wmm, hmm, hi, hx, hy, hw, hh, lo, lx, ly, lw, lh, size] = r;
    return { n, year, early: isEarly, w: wmm / 1000, h: hmm / 1000, hi: hi + offHi, hx, hy, hw, hh, lo: lo + offLo, lx, ly, lw, lh, size };
  };
  const numbered = data.pieces.map((r) => toPiece(r, 0, 0, false)).sort((a, b) => (a.n < b.n ? -1 : a.n > b.n ? 1 : 0));
  const earlyPieces = early ? early.pieces.map((r) => toPiece(r, data.hi.length, data.lo.length, true)) : [];
  const pieces = earlyPieces.concat(numbered);
  pieces.forEach((p, i) => { p.i = i; });
  world.pieces = pieces; world.numbered = numbered; world.early = earlyPieces; world.first = numbered[0];
  for (const p of pieces) world.byN.set(p.n, p);
  world.chunks = hiList.map(([url, h], i) => ({ i, url, h, pieces: [], room: null, mesh: null, group: null, state: 'none', tex: null, mat: null, box: new THREE.Box3(), lo: null, dist: 99 }));
  for (const p of pieces) { world.chunks[p.hi].pieces.push(p); world.chunks[p.hi].lo = p.lo; }

  const firstK = numbered.filter((p) => p.n <= '001000'), rest = numbered.filter((p) => p.n > '001000');
  const bySize = (...s) => rest.filter((p) => s.includes(p.size));
  const add = (r) => { r.index = world.rooms.length; r.built = false; world.rooms.push(r); return r; };

  const salon = add(planSalon(firstK, earlyPieces.length > 0)); setTf(salon, 0, 0, 0);
  if (earlyPieces.length) {
    setTf(add(planVestibule('Passage to Early Work')), Math.PI, 0, 0.4);
    const er = add(planAlcoveGallery({ key: 'early', early: true, style: 'gallery', title: 'Early Work', name: 'Early Work', back: 'The First Thousand' }, earlyPieces));
    setTf(er, Math.PI, 0, 0.4 + VEST_L + WALL_T);
  }
  setTf(add(planVestibule('Passage to the Long Gallery')), 0, 0, -salon.len - 0.4);
  const long = add(planLong(bySize('XS'))); setTf(long, 0, 0, -salon.len - 0.8 - VEST_L);
  const wings = [
    ['small', 'The Small Gallery', 'Small', bySize('S'), 'gallery', '9 × 12 in sheets'],
    ['medium', 'The Medium Gallery', 'Medium', bySize('M'), 'gallery', '9 × 24 in sheets'],
    ['large', 'The Large & X-Large Hall', 'Large & X-Large', bySize('L', 'XL'), 'grand', '22 × 30 in and larger'],
  ];
  wings.forEach(([key, title, short, list, style, sizeLine], i) => {
    const door = long.doors[i];
    if (!list.length || !door) return;
    const zd = toW(long, LH, door.z)[1];
    setTf(add(planVestibule(`Passage to ${title}`)), -Math.PI / 2, LH + LW_T, zd);
    const g = add(planAlcoveGallery({ key, style, title, short, name: title, back: 'The Long Gallery', sizeLine }, list));
    setTf(g, -Math.PI / 2, LH + LW_T + VEST_L + WALL_T, zd);
    door.room = g;
  });
  world.salon = salon; world.long = long;

  for (const r of world.rooms) { for (const w of r.walls) w.room = r; finalizeRoom(r); }
  for (const p of pieces) {
    const w = p.wall;
    p.room = w.room;
    p.pos = wallPointW(w, p.u, p.y, FIELD_N + p.depth);
    const ch = world.chunks[p.hi];
    ch.room = w.room;
    const half = new THREE.Vector3(Math.abs(w.W.U[0]) * p.ow / 2 + 0.05, p.oh / 2, Math.abs(w.W.U[1]) * p.ow / 2 + 0.05);
    ch.box.expandByPoint(p.pos.clone().add(half)); ch.box.expandByPoint(p.pos.clone().sub(half));
  }
  for (const room of world.rooms) room.chunks = world.chunks.filter((c) => c.room === room);
}

/* ------------------------------------------------------------------ shared models */
const MODELS = {};
function lathe(profile, seg = 14) { return new THREE.LatheGeometry(profile.map(([r, y]) => new THREE.Vector2(r, y)), seg); }
function buildModels() {
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), s = new THREE.Vector3(1, 1, 1), p = new THREE.Vector3();
  const at = (x, y, z, rx = 0, ry = 0, rz = 0, sc = 1) => m.compose(p.set(x, y, z), q.setFromEuler(e.set(rx, ry, rz)), s.set(sc, sc, sc));
  // Chandelier: origin at the bottom finial; 8 lower + 6 upper candles.
  {
    const brass = new Buf(), wax = new Buf(), flames = [];
    brass.addGeometry(lathe([[0, 0], [0.025, 0.0], [0.05, 0.05], [0.03, 0.1], [0.02, 0.15], [0.07, 0.22], [0.1, 0.28], [0.07, 0.34], [0.028, 0.38], [0.022, 0.6], [0.06, 0.65], [0.035, 0.72], [0.02, 0.75], [0.02, 1.02], [0, 1.02]], 18), at(0, 0, 0));
    const tiers = [[0.66, 0.3, 8], [0.38, 0.62, 6]];
    for (const [rad, y, count] of tiers) {
      brass.addGeometry(new THREE.TorusGeometry(rad, 0.013, 6, 56), at(0, y, 0, Math.PI / 2));
      for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2 + (count === 6 ? 0.26 : 0), cx = Math.cos(a), cz = Math.sin(a);
        brass.addGeometry(new THREE.CylinderGeometry(0.01, 0.01, rad, 6), at(cx * rad / 2, y, cz * rad / 2, 0, -a, Math.PI / 2));
        brass.addGeometry(new THREE.CylinderGeometry(0.012, 0.016, 0.1, 8), at(cx * rad, y + 0.05, cz * rad));
        brass.addGeometry(new THREE.CylinderGeometry(0.045, 0.03, 0.014, 12), at(cx * rad, y + 0.105, cz * rad));
        wax.addGeometry(new THREE.CylinderGeometry(0.016, 0.017, 0.13, 8), at(cx * rad, y + 0.177, cz * rad));
        flames.push([cx * rad, y + 0.265, cz * rad]);
      }
    }
    brass.addGeometry(new THREE.TorusGeometry(0.16, 0.01, 6, 24), at(0, 0.96, 0, Math.PI / 2));
    MODELS.chandelier = { brass: brass.geometry(), wax: wax.geometry(), flames };
  }
  // Chesterfield armchair, faces +Z.
  {
    const lea = new Buf(), wood = new Buf();
    const rb = (w, h, d, r) => new RoundedBoxGeometry(w, h, d, 3, r);
    for (const [x, z] of [[-0.39, -0.33], [0.39, -0.33], [-0.39, 0.33], [0.39, 0.33]]) wood.addGeometry(lathe([[0, 0], [0.035, 0], [0.045, 0.03], [0.03, 0.08], [0, 0.08]], 10), at(x, 0, z));
    lea.addGeometry(rb(0.96, 0.34, 0.84, 0.05), at(0, 0.25, 0));
    lea.addGeometry(rb(0.64, 0.13, 0.64, 0.05), at(0, 0.48, 0.08));
    lea.addGeometry(rb(0.96, 0.4, 0.2, 0.06), at(0, 0.6, -0.32));
    for (const sx of [-1, 1]) {
      lea.addGeometry(rb(0.17, 0.32, 0.84, 0.05), at(sx * 0.395, 0.56, 0));
      lea.addGeometry(new THREE.CylinderGeometry(0.095, 0.095, 0.86, 16), at(sx * 0.41, 0.72, 0, Math.PI / 2));
    }
    lea.addGeometry(new THREE.CylinderGeometry(0.095, 0.095, 0.98, 16), at(0, 0.8, -0.33, 0, 0, Math.PI / 2));
    MODELS.chair = { leather: lea.geometry(), wood: wood.geometry() };
  }
  // Museum bench, long axis X.
  {
    const lea = new Buf(), wood = new Buf();
    lea.addGeometry(new RoundedBoxGeometry(1.7, 0.15, 0.56, 3, 0.05), at(0, 0.44, 0));
    wood.box(0, 0.34, 0, 1.6, 0.07, 0.48, { grain: 'x' });
    const leg = lathe([[0, 0], [0.032, 0], [0.04, 0.03], [0.026, 0.08], [0.034, 0.14], [0.022, 0.22], [0.03, 0.3], [0.03, 0.34], [0, 0.34]], 12);
    for (const [x, z] of [[-0.74, -0.2], [0.74, -0.2], [-0.74, 0.2], [0.74, 0.2]]) wood.addGeometry(leg, at(x, 0, z));
    MODELS.bench = { leather: lea.geometry(), wood: wood.geometry() };
  }
  // Round side table with a chamberstick.
  {
    const wood = new Buf(), brass = new Buf(), wax = new Buf();
    wood.addGeometry(new THREE.CylinderGeometry(0.3, 0.3, 0.035, 32), at(0, 0.62, 0));
    wood.addGeometry(new THREE.CylinderGeometry(0.27, 0.26, 0.06, 32), at(0, 0.575, 0));
    wood.addGeometry(lathe([[0, 0.08], [0.05, 0.08], [0.06, 0.14], [0.035, 0.26], [0.05, 0.38], [0.03, 0.5], [0.045, 0.55], [0, 0.55]], 14), at(0, 0, 0));
    for (let i = 0; i < 3; i++) { const a = i * Math.PI * 2 / 3; wood.box(Math.cos(a) * 0.14, 0.05, Math.sin(a) * 0.14, 0.3, 0.06, 0.05, { grain: 'x' }); }
    brass.addGeometry(new THREE.CylinderGeometry(0.07, 0.075, 0.012, 20), at(0.08, 0.644, 0.04));
    brass.addGeometry(lathe([[0, 0], [0.022, 0], [0.016, 0.05], [0.026, 0.06], [0, 0.06]], 12), at(0.08, 0.65, 0.04));
    wax.addGeometry(new THREE.CylinderGeometry(0.019, 0.02, 0.16, 10), at(0.08, 0.79, 0.04));
    MODELS.sideTable = { wood: wood.geometry(), brass: brass.geometry(), wax: wax.geometry(), flames: [[0.08, 0.895, 0.04]] };
  }
  // Floor candelabrum (three lights).
  {
    const brass = new Buf(), wax = new Buf(), flames = [];
    brass.addGeometry(lathe([[0, 0.02], [0.16, 0.02], [0.16, 0.05], [0.07, 0.1], [0.03, 0.2], [0.045, 0.4], [0.02, 0.5], [0.02, 1.25], [0.05, 1.3], [0.03, 1.36], [0, 1.36]], 16), at(0, 0, 0));
    for (const x of [-0.2, 0, 0.2]) {
      if (x) brass.addGeometry(new THREE.CylinderGeometry(0.01, 0.01, 0.2, 6), at(x / 2, 1.34, 0, 0, 0, Math.PI / 2));
      brass.addGeometry(new THREE.CylinderGeometry(0.045, 0.03, 0.02, 14), at(x, 1.38 + (x ? 0 : 0.06), 0));
      wax.addGeometry(new THREE.CylinderGeometry(0.02, 0.021, 0.24, 10), at(x, 1.51 + (x ? 0 : 0.06), 0));
      flames.push([x, 1.66 + (x ? 0 : 0.06), 0]);
    }
    MODELS.candelabrum = { brass: brass.geometry(), wax: wax.geometry(), flames };
  }
  // Library table with a three-light candelabra, books and an open folio. Long axis X.
  {
    const wood = new Buf(), brass = new Buf(), wax = new Buf(), felt = new Buf(), cloth = new Buf(), paper = new Buf(), flames = [];
    wood.box(0, 0.755, 0, 1.9, 0.05, 0.95, { grain: 'x' });
    wood.box(0, 0.68, 0, 1.72, 0.1, 0.8, { grain: 'x' });
    felt.box(0, 0.782, 0, 1.6, 0.004, 0.68);
    const leg = lathe([[0, 0], [0.04, 0], [0.05, 0.05], [0.032, 0.14], [0.05, 0.3], [0.03, 0.48], [0.042, 0.6], [0.042, 0.64], [0, 0.64]], 14);
    for (const [x, z] of [[-0.82, -0.36], [0.82, -0.36], [-0.82, 0.36], [0.82, 0.36]]) wood.addGeometry(leg, at(x, 0, z));
    brass.addGeometry(lathe([[0, 0], [0.09, 0], [0.05, 0.04], [0.025, 0.1], [0.018, 0.34], [0, 0.34]], 14), at(-0.45, 0.785, 0.05));
    brass.addGeometry(new THREE.CylinderGeometry(0.01, 0.01, 0.36, 6), at(-0.45, 1.1, 0.05, 0, 0, Math.PI / 2));
    for (const dx of [-0.17, 0, 0.17]) {
      brass.addGeometry(new THREE.CylinderGeometry(0.035, 0.025, 0.016, 12), at(-0.45 + dx, 1.13 + (dx ? 0 : 0.05), 0.05));
      wax.addGeometry(new THREE.CylinderGeometry(0.016, 0.017, 0.18, 10), at(-0.45 + dx, 1.23 + (dx ? 0 : 0.05), 0.05));
      flames.push([-0.45 + dx, 1.35 + (dx ? 0 : 0.05), 0.05]);
    }
    const bookCols = ['#5e1a14', '#1d2740', '#2a3a26', '#6b4a22', '#3b1d2e'];
    let y = 0.785;
    for (let i = 0; i < 4; i++) { const h = 0.035 + R() * 0.03; cloth.box(0.55 + (R() - 0.5) * 0.04, y + h / 2, -0.15, 0.26 - i * 0.02, h, 0.19 - i * 0.01, { color: lin(bookCols[i % 5]) }); y += h; }
    paper.addGeometry(new THREE.BoxGeometry(0.22, 0.012, 0.3), at(0.17, 0.79, 0.12, 0, 0.1, 0.06));
    paper.addGeometry(new THREE.BoxGeometry(0.22, 0.012, 0.3), at(-0.05, 0.79, 0.14, 0, 0.1, -0.06));
    MODELS.libTable = { wood: wood.geometry(), brass: brass.geometry(), wax: wax.geometry(), felt: felt.geometry(), cloth: cloth.geometry(), paper: paper.geometry(), flames };
  }
}
function place(group, model, x, y, z, ry = 0, sc = 1, room = null) {
  const g = new THREE.Group(); g.position.set(x, y, z); g.rotation.y = ry; g.scale.setScalar(sc);
  for (const [k, geo] of Object.entries(model)) {
    if (k === 'flames') continue;
    const mat = { leather: M.leather, wood: M.mahog, brass: M.brass, wax: M.wax, felt: M.felt, cloth: M.cloth, paper: M.paper }[k];
    if (mat) g.add(new THREE.Mesh(geo, mat));
  }
  group.add(g);
  g.updateMatrix(); // flames in the room's local frame
  const flames = (model.flames || []).map((f) => new THREE.Vector3(...f).applyMatrix4(g.matrix));
  return { g, flames };
}

/* ------------------------------------------------------------------ room building */
function newBufs() {
  return { walnut: new Buf(), field: new Buf(), mahog: new Buf(), burl: new Buf(), fluted: new Buf(), ceil: new Buf(), floor: new Buf(), brass: new Buf(), iron: new Buf(), wax: new Buf(), rug: new Buf(), rugWide: new Buf(), books0: new Buf(), books1: new Buf(), books2: new Buf(),
    contact: new Buf(), scallop: new Buf() };
}
function flushBufs(group, B) {
  for (const [k, buf] of Object.entries(B)) {
    if (!buf.n) continue;
    const mat = k.startsWith('books') ? M.books[+k.slice(5)] : M[k];
    const mesh = new THREE.Mesh(buf.geometry(), mat);
    if (k === 'contact' || k === 'scallop') mesh.renderOrder = 1;
    mesh.matrixAutoUpdate = false;
    group.add(mesh);
  }
}
const UP = new THREE.Vector3(0, 1, 0), DOWN = new THREE.Vector3(0, -1, 0);
function vec(w, u, y, n) { return wallPoint(w, u, y, n); }
function upQuad(buf, x0, x1, z0, z1, y, uvScale, col) {
  const a = new THREE.Vector3(x0, y, z0), b = new THREE.Vector3(x1, y, z0), c = new THREE.Vector3(x1, y, z1), d = new THREE.Vector3(x0, y, z1);
  const uv = (p) => [p.x / uvScale[0], p.z / uvScale[1]];
  buf.quad(a, b, c, d, UP, [uv(a), uv(b), uv(c), uv(d)], col);
}
function ceilQuad(buf, x0, x1, z0, z1, y) {
  const a = new THREE.Vector3(x0, y, z0), b = new THREE.Vector3(x1, y, z0), c = new THREE.Vector3(x1, y, z1), d = new THREE.Vector3(x0, y, z1);
  const uv = (p) => [p.x / 1.2, p.z / 2.4];
  buf.quad(a, b, c, d, DOWN, [uv(a), uv(b), uv(c), uv(d)]);
}
function rugQuad(buf, cx, cz, w, l, rot = false) {
  const hw = w / 2, hl = l / 2, y = 0.006;
  const pts = rot ? [[-hl, -hw], [hl, -hw], [hl, hw], [-hl, hw]] : [[-hw, -hl], [hw, -hl], [hw, hl], [-hw, hl]];
  const V = pts.map(([x, z]) => new THREE.Vector3(cx + x, y, cz + z));
  const uvs = rot ? [[0, 0], [0, 1], [1, 1], [1, 0]] : [[0, 1], [1, 1], [1, 0], [0, 0]];
  buf.quad(V[0], V[1], V[2], V[3], UP, uvs);
}
function contactShadow(buf, cx, cz, w, l) {
  const V = [[-w / 2, -l / 2], [w / 2, -l / 2], [w / 2, l / 2], [-w / 2, l / 2]].map(([x, z]) => new THREE.Vector3(cx + x, 0.009, cz + z));
  buf.quad(V[0], V[1], V[2], V[3], UP, [[0, 0], [1, 0], [1, 1], [0, 1]]);
}

/** Low wainscot (chair rail at 0.6 m), figured field to `fieldTop`, picture rail and frieze up to `top`. */
function decorateWall(B, w, top = PART_H, opts = {}) {
  const L = w.len, ft = Math.min(top, opts.fieldTop ?? 3.92);
  wbox(B.mahog, w, 0, L, 0, 0.15, 0, 0.03, { grain: 'u', skip: ['-y'] });
  wbox(B.walnut, w, 0, L, 0.15, 0.56, 0, 0.012, { grain: 'u' });
  const np = Math.max(1, Math.round(L / 0.9)), pw = L / np;
  for (let i = 0; i < np; i++) {
    wbox(B.mahog, w, i * pw + 0.05, (i + 1) * pw - 0.05, 0.2, 0.52, 0.012, 0.02, { grain: 'u' });
    wbox(B.burl, w, i * pw + 0.085, (i + 1) * pw - 0.085, 0.23, 0.49, 0.02, 0.03, { grain: 'y', scale: [0.6, 0.9] });
  }
  wbox(B.mahog, w, 0, L, 0.55, 0.62, 0, 0.05, { grain: 'u' });
  wbox(B.field, w, 0, L, 0.62, ft, 0, FIELD_N, { grain: 'y', scale: [1.4, 2.8] });
  if (top > ft) {
    wbox(B.mahog, w, 0, L, ft, ft + 0.08, 0, 0.045, { grain: 'u' });
    wbox(B.walnut, w, 0, L, ft + 0.08, top, 0, 0.02, { grain: 'u' });
    if (!opts.noCornice) wbox(B.mahog, w, 0, L, top - 0.12, top, 0.02, 0.09, { grain: 'u' });
  }
}
function upperPanels(B, w, y0, y1) {
  wbox(B.walnut, w, 0, w.len, y0, y1, 0, 0.012, { grain: 'u' });
  const np = Math.max(1, Math.round(w.len / 1.6)), pw = w.len / np;
  for (let i = 0; i < np; i++) {
    wbox(B.mahog, w, i * pw + 0.08, (i + 1) * pw - 0.08, y0 + 0.15, y1 - 0.25, 0.012, 0.022, { grain: 'u' });
    wbox(B.burl, w, i * pw + 0.13, (i + 1) * pw - 0.13, y0 + 0.2, y1 - 0.3, 0.022, 0.03, { grain: 'y', scale: [0.8, 1.2] });
  }
  wbox(B.mahog, w, 0, w.len, y1 - 0.16, y1, 0.012, 0.11, { grain: 'u' });
}
function bookshelves(B, w, u0, u1, y0, y1, depth = 0.32, rows = null) {
  const span = u1 - u0;
  const n = rows || Math.max(1, Math.round((y1 - y0) / 0.4));
  const rh = (y1 - y0) / n;
  wbox(B.walnut, w, u0, u1, y0, y1, 0, 0.02, { grain: 'u' });
  const bays = Math.max(1, Math.round(span / 1.1)), bw = span / bays;
  for (let r = 0; r < n; r++) {
    const yb = y0 + r * rh;
    wbox(B.mahog, w, u0, u1, yb, yb + 0.035, 0, depth, { grain: 'u' });
    const k = Math.floor(R() * 3), bk = B['books' + k];
    const a = vec(w, u0, yb + 0.035, 0.05), b = vec(w, u1, yb + 0.035, 0.05), c = vec(w, u1, yb + rh, 0.05), d = vec(w, u0, yb + rh, 0.05);
    const off = R() * 4, rep = span / 1.0, nrm = new THREE.Vector3(w.N[0], 0, w.N[1]);
    bk.quad(a, b, c, d, nrm, [[off, 0], [off + rep, 0], [off + rep, 1], [off, 1]]);
  }
  wbox(B.mahog, w, u0, u1, y1 - 0.02, y1 + 0.07, 0, depth + 0.04, { grain: 'u' });
  for (let i = 0; i <= bays; i++) { const u = u0 + i * bw; wbox(B.mahog, w, Math.max(u0, u - 0.025), Math.min(u1, u + 0.025), y0, y1, 0, depth + 0.01, { grain: 'y' }); }
}
function pilaster(B, x, zc, faceN, width, h0 = 0, h1 = 4.0) {
  // x: the aisle-facing surface; faceN: +1 if it faces +X.
  const out = (d) => x + faceN * d;
  B.mahog.box(out(0.06), h0 + 0.16, zc, 0.12, 0.32, width + 0.22, { grain: 'z' });
  B.fluted.box(out(0.035), (h0 + 0.32 + h1) / 2, zc, 0.07, h1 - h0 - 0.32, width, { grain: 'y', scale: [width, 1.2], ou: 0, ov: 0 });
  B.mahog.box(out(0.06), h1 + 0.06, zc, 0.12, 0.12, width + 0.2, { grain: 'z' });
  B.mahog.box(out(0.08), h1 + 0.17, zc, 0.16, 0.1, width + 0.3, { grain: 'z' });
}

function hiUV(p, atlasH) {
  const W = world.atlas, x0 = (p.hx + 0.5) / W, x1 = (p.hx + p.hw - 0.5) / W, yT = 1 - (p.hy + 0.5) / atlasH, yB = 1 - (p.hy + p.hh - 0.5) / atlasH;
  return [[x0, yB], [x1, yB], [x1, yT], [x0, yT]];
}
function loUV(p) {
  const W = world.atlas, H = world.loAtlases[p.lo].h, x0 = (p.lx + 0.5) / W, x1 = (p.lx + p.lw - 0.5) / W, yT = 1 - (p.ly + 0.5) / H, yB = 1 - (p.ly + p.lh - 0.5) / H;
  return [[x0, yB], [x1, yB], [x1, yT], [x0, yT]];
}
const FRAME_COLS = { black: lin('#16110e'), walnut: lin('#3a2416'), mahog: lin('#5a2414'), gilt: lin('#d9b36a'), oldgilt: lin('#a8823c') };
const CREAM = lin('#e9dcc0');

/** One framed drawing into its chunk's buffers: drop shadow, frame, mat and the art quad. */
function addFramed(CB, p, w, atlasH) {
  const U = new THREE.Vector3(w.U[0], 0, w.U[1]), N = new THREE.Vector3(w.N[0], 0, w.N[1]), Y = UP;
  const C = wallPoint(w, p.u, p.y, FIELD_N);
  const P = (a, b, n) => new THREE.Vector3(C.x + U.x * a + N.x * n, C.y + b, C.z + U.z * a + N.z * n);
  const hw = p.ow / 2, hh = p.oh / 2, iw = p.w / 2 + p.mat, ih = p.h / 2 + p.mat;
  const nf = p.depth, nm = 0.008;
  const lit = (a, b) => { const l = lampLight(w, p.u + a, p.y + b); return [l, l * 0.94, l * 0.84]; };
  CB.shadow.quad(P(-hw - 0.03, -hh - 0.05, 0.002), P(hw + 0.03, -hh - 0.05, 0.002), P(hw + 0.03, hh + 0.01, 0.002), P(-hw - 0.03, hh + 0.01, 0.002), N, [[0, 0], [1, 0], [1, 1], [0, 1]]);
  const r = hash(p.n);
  const kind = r < 0.42 ? 'black' : r < 0.6 ? 'walnut' : r < 0.74 ? 'mahog' : r < 0.9 ? 'gilt' : 'oldgilt';
  const fb = kind.includes('gilt') ? CB.frameGilt : CB.frameDark, fc = FRAME_COLS[kind];
  const dim = (k) => [fc[0] * k, fc[1] * k, fc[2] * k];
  const front = [[-hw, ih, -hw, hh, hw, hh, hw, ih], [-hw, -hh, -hw, -ih, hw, -ih, hw, -hh], [-hw, -ih, -hw, ih, -iw, ih, -iw, -ih], [iw, -ih, iw, ih, hw, ih, hw, -ih]];
  for (const f of front) fb.quad(P(f[0], f[1], nf), P(f[2], f[3], nf), P(f[4], f[5], nf), P(f[6], f[7], nf), N, null, fc);
  const NY = new THREE.Vector3(0, -1, 0), NU = U.clone().negate();
  fb.quad(P(-hw, hh, 0), P(hw, hh, 0), P(hw, hh, nf), P(-hw, hh, nf), Y, null, dim(0.8));
  fb.quad(P(-hw, -hh, 0), P(hw, -hh, 0), P(hw, -hh, nf), P(-hw, -hh, nf), NY, null, dim(0.5));
  fb.quad(P(-hw, -hh, 0), P(-hw, hh, 0), P(-hw, hh, nf), P(-hw, -hh, nf), NU, null, dim(0.65));
  fb.quad(P(hw, -hh, 0), P(hw, hh, 0), P(hw, hh, nf), P(hw, -hh, nf), U, null, dim(0.65));
  fb.quad(P(-iw, ih, nm), P(iw, ih, nm), P(iw, ih, nf), P(-iw, ih, nf), NY, null, dim(0.45));
  fb.quad(P(-iw, -ih, nm), P(iw, -ih, nm), P(iw, -ih, nf), P(-iw, -ih, nf), Y, null, dim(0.85));
  fb.quad(P(-iw, -ih, nm), P(-iw, ih, nm), P(-iw, ih, nf), P(-iw, -ih, nf), U, null, dim(0.6));
  fb.quad(P(iw, -ih, nm), P(iw, ih, nm), P(iw, ih, nf), P(iw, -ih, nf), NU, null, dim(0.6));
  if (p.mat > 0) {
    const cs = [[-iw, -ih], [iw, -ih], [iw, ih], [-iw, ih]].map(([a, b]) => { const l = lit(a, b); return [CREAM[0] * l[0], CREAM[1] * l[1], CREAM[2] * l[2]]; });
    CB.mat.quad(P(-iw, -ih, nm), P(iw, -ih, nm), P(iw, ih, nm), P(-iw, ih, nm), N, null, cs);
  }
  const aw = p.w / 2, ah = p.h / 2, na = nm + 0.0018;
  const cs = [[-aw, -ah], [aw, -ah], [aw, ah], [-aw, ah]].map(([a, b]) => lit(a, b));
  CB.art.quad(P(-aw, -ah, na), P(aw, -ah, na), P(aw, ah, na), P(-aw, ah, na), N, hiUV(p, atlasH), cs, loUV(p));
}
function addLamps(B, w, glows) {
  const N = new THREE.Vector3(w.N[0], 0, w.N[1]), ly = w.lampY ?? LAMP_Y;
  for (const lu of w.lamps) {
    wbox(B.brass, w, lu - 0.04, lu + 0.04, ly + 0.03, ly + 0.13, FIELD_N, FIELD_N + 0.02);
    const arm = vec(w, lu, ly + 0.1, 0.11);
    B.brass.box(arm.x, arm.y, arm.z, Math.abs(N.x) * 0.2 + 0.016, 0.016, Math.abs(N.z) * 0.2 + 0.016);
    const hood = vec(w, lu, ly + 0.07, 0.22);
    const alongX = Math.abs(w.U[0]) > 0.5;
    B.brass.box(hood.x, hood.y, hood.z, alongX ? 0.46 : 0.07, 0.06, alongX ? 0.07 : 0.46);
    glows.push([hood.x, hood.y - 0.04, hood.z, 0.32, 'halo']);
    const s = 1.7, top = ly + 0.15, bot = Math.max(0.62, top - 3.0);
    B.scallop.quad(vec(w, lu - s / 2, bot, FIELD_N + 0.001), vec(w, lu + s / 2, bot, FIELD_N + 0.001), vec(w, lu + s / 2, top, FIELD_N + 0.001), vec(w, lu - s / 2, top, FIELD_N + 0.001), N, [[0, 0], [1, 0], [1, 1], [0, 1]]);
  }
}
/* Frames, mats, shadows and art live in one small group per atlas, so far-off stretches can be hidden. */
function chunkBufs(map, ch) {
  let cb = map.get(ch.i);
  if (!cb) { const n = ch.pieces.length; cb = { art: new Buf(n * 6 + 6, true), frameDark: new Buf(n * 72 + 72), frameGilt: new Buf(n * 30 + 72), mat: new Buf(n * 6 + 6), shadow: new Buf(n * 6 + 6) }; map.set(ch.i, cb); }
  return cb;
}
function hangArt(B, glows, walls) {
  const map = new Map();
  for (const w of walls) {
    addLamps(B, w, glows);
    for (const p of w.pieces) addFramed(chunkBufs(map, world.chunks[p.hi]), p, w, world.chunks[p.hi].h);
  }
  return map;
}
function flushChunks(group, map) {
  for (const [i, cb] of map) {
    const ch = world.chunks[i], g = new THREE.Group();
    for (const k of ['shadow', 'frameDark', 'frameGilt', 'mat']) {
      if (!cb[k].n) continue;
      const m = new THREE.Mesh(cb[k].geometry(), M[k]); m.matrixAutoUpdate = false; if (k === 'shadow') m.renderOrder = 1; g.add(m);
    }
    const art = new THREE.Mesh(cb.art.geometry(), M.artPending); art.matrixAutoUpdate = false; g.add(art);
    ch.mesh = art; ch.group = g; group.add(g);
    applyChunkMaterial(ch);
  }
}
function addChandelier(group, glows, room, x, z, scale = 1, bottom = 4.55, ceil = CEIL) {
  const g = new THREE.Group(); g.position.set(x, bottom, z); g.scale.setScalar(scale);
  g.add(new THREE.Mesh(MODELS.chandelier.brass, M.brass), new THREE.Mesh(MODELS.chandelier.wax, M.wax));
  const top = bottom + 1.02 * scale;
  const chain = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.01, ceil - top, 6), M.iron); chain.position.set(x, (ceil + top) / 2, z);
  const rose = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.24, 0.08, 20), M.brass); rose.position.set(x, ceil - 0.04, z);
  group.add(g, chain, rose);
  for (const f of MODELS.chandelier.flames) {
    const v = new THREE.Vector3(...f).multiplyScalar(scale).add(g.position);
    glows.push([v.x, v.y, v.z, 0.5 * scale, 'halo'], [v.x, v.y, v.z, 0.075 * scale, 'flame']);
  }
  room.lights.push({ pos: new THREE.Vector3(x, bottom + 0.3, z), color: 0xffb46a, power: 34 * scale * scale, flicker: 0.06 });
}
function addFlames(glows, flames, size = 1) { for (const v of flames) glows.push([v.x, v.y, v.z, 0.32 * size, 'halo'], [v.x, v.y, v.z, 0.06 * size, 'flame']); }
function flushGlows(group, glows) {
  for (const kind of ['halo', 'flame']) {
    const list = glows.filter((g) => g[4] === kind); if (!list.length) continue;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(list.flatMap((g) => [g[0], g[1], g[2]])), 3));
    geo.setAttribute('aSize', new THREE.BufferAttribute(new Float32Array(list.map((g) => g[3])), 1));
    geo.setAttribute('aPhase', new THREE.BufferAttribute(new Float32Array(list.map(() => R())), 1));
    const pts = new THREE.Points(geo, kind === 'halo' ? M.glowHalo : M.glowFlame); pts.renderOrder = 2; pts.frustumCulled = false;
    group.add(pts);
  }
}
function plaqueOn(group, w, u, y, tex, pw, ph, kind = 'brass', n = 0.03) {
  const pm = plaqueMesh(tex, pw, ph, kind); pm.position.copy(wallPoint(w, u, y, n)); pm.rotation.y = Math.atan2(w.N[0], w.N[1]); group.add(pm); return pm;
}
function doorPlaque(label) {
  return plaque(1024, 240, (c, W, H) => {
    engrave(c, label.small, W / 2, H * 0.3, `46px "IM Fell English SC", Georgia, serif`);
    engrave(c, label.big, W / 2, H * 0.66, `italic ${label.big.length > 22 ? 66 : 84}px "IM Fell English", Georgia, serif`);
  });
}
/** Casing round a door. `wc` is a wall frame whose u = 0 is the door's centre line, facing the room. */
function doorCasing(B, group, wc, label, dw = DOOR_W, dh = DOOR_H) {
  const D2 = dw / 2;
  for (const s of [-1, 1]) wbox(B.mahog, wc, s * (D2 + 0.1) - 0.11, s * (D2 + 0.1) + 0.11, 0, dh + 0.22, 0, 0.07, { grain: 'y' });
  wbox(B.mahog, wc, -D2 - 0.21, D2 + 0.21, dh, dh + 0.24, 0, 0.07, { grain: 'u' });
  wbox(B.mahog, wc, -D2 - 0.4, D2 + 0.4, dh + 0.24, dh + 0.36, 0, 0.16, { grain: 'u' });
  if (!label) return;
  wbox(B.burl, wc, -D2 - 0.05, D2 + 0.05, dh + 0.36, dh + 0.95, 0, 0.03, { grain: 'u' });
  wbox(B.mahog, wc, -D2 - 0.3, D2 + 0.3, dh + 0.95, dh + 1.05, 0, 0.12, { grain: 'u' });
  plaqueOn(group, wc, 0, dh + 0.655, doorPlaque(label), 1.9, 0.445, 'brass', 0.032);
}
/** Door reveal: thin mahogany linings through a wall's thickness. Axis-aligned, in room-local coords. */
function doorLining(B, cx, cz, alongX, thick, dw = DOOR_W, dh = DOOR_H) {
  const D2 = dw / 2;
  for (const s of [-1, 1]) {
    if (alongX) B.mahog.box(cx + s * (D2 - 0.015), dh / 2, cz, 0.03, dh, thick, { grain: 'y' });
    else B.mahog.box(cx, dh / 2, cz + s * (D2 - 0.015), thick, dh, 0.03, { grain: 'y' });
  }
  if (alongX) { B.mahog.box(cx, dh - 0.015, cz, dw, 0.03, thick, { grain: 'x' }); B.mahog.box(cx, 0.006, cz, dw, 0.012, thick, { grain: 'x' }); }
  else { B.mahog.box(cx, dh - 0.015, cz, thick, 0.03, dw, { grain: 'z' }); B.mahog.box(cx, 0.006, cz, thick, 0.012, dw, { grain: 'z' }); }
}

/** The entry wall of an alcove gallery (local z 0..WALL_T), door centred on x = 0. */
function buildDivider(B, group, inside, outside) {
  const X = OUT_X + 0.35;
  for (const s of [-1, 1]) B.walnut.box(s * (X + DOOR_W / 2) / 2, CEIL / 2, WALL_T / 2, X - DOOR_W / 2, CEIL, WALL_T, { grain: 'y' });
  B.walnut.box(0, (DOOR_H + CEIL) / 2, WALL_T / 2, DOOR_W, CEIL - DOOR_H, WALL_T, { grain: 'x' });
  doorLining(B, 0, WALL_T / 2, true, WALL_T);
  // inside face: aisle segments below the cornice (the end blocks cover the rest), panels above
  for (const [x0, x1] of [[-HALF, -DOOR_W / 2 - 0.22], [DOOR_W / 2 + 0.22, HALF]]) decorateWall(B, makeWall(x1, 0, 0, -1, x1 - x0), PART_H, { noCornice: true });
  upperPanels(B, makeWall(OUT_X, 0, 0, -1, OUT_X * 2), PART_H, CEIL);
  doorCasing(B, group, makeWall(0, 0, 0, -1, 0), inside);
  // outside face, seen from the passage
  for (const [x0, x1] of [[-OUT_X, -DOOR_W / 2 - 0.22], [DOOR_W / 2 + 0.22, OUT_X]]) decorateWall(B, makeWall(x0, WALL_T, 0, 1, x1 - x0), 4.3, { fieldTop: 2.95 });
  doorCasing(B, group, makeWall(0, WALL_T, 0, 1, 0), outside);
}

function wingPlaque(room) {
  return plaque(1024, 720, (c, W, H) => {
    if (room.early) {
      engrave(c, 'BEFORE THE STAMPS', W / 2, H * 0.14, `52px "IM Fell English SC", Georgia, serif`);
      engrave(c, 'Early Work', W / 2, H * 0.37, `italic 170px "IM Fell English", Georgia, serif`);
      engrave(c, `${fmt(room.list.length)} unnumbered pieces`, W / 2, H * 0.6, `52px "Special Elite", "Courier New", monospace`);
      c.font = `italic 44px "IM Fell English", Georgia, serif`;
      wrapLines(c, 'School and early-career work, 2018 to 2020, the retired Water Series, then the studies and zines that never got a number.', W * 0.84)
        .forEach((l, i) => engrave(c, l, W / 2, H * (0.73 + i * 0.075), c.font));
      return;
    }
    engrave(c, 'THE GALLERY OF', W / 2, H * 0.13, `50px "IM Fell English SC", Georgia, serif`);
    engrave(c, room.short, W / 2, H * 0.36, `italic ${room.short.length > 8 ? 130 : 190}px "IM Fell English", Georgia, serif`);
    engrave(c, room.sizeLine, W / 2, H * 0.56, `italic 50px "IM Fell English", Georgia, serif`);
    engrave(c, room.range, W / 2, H * 0.7, `52px "Special Elite", "Courier New", monospace`);
    engrave(c, `${fmt(room.list.length)} drawings, hung in stamp order`, W / 2, H * 0.84, `italic 46px "IM Fell English", Georgia, serif`);
  });
}
function buildAlcoveGallery(room) {
  const group = new THREE.Group(), B = newBufs(), glows = [];
  const { z1, zA, zbLast } = room, z0 = 0;
  const len = z0 - z1;
  upQuad(B.floor, -OUT_X, OUT_X, z1, z0, 0, [1.6, 1.6]);
  ceilQuad(B.ceil, -OUT_X, OUT_X, z0, z1, CEIL);
  for (const x of [-HALF, 0, HALF]) B.mahog.box(x, CEIL - 0.17, (z0 + z1) / 2, 0.3, 0.34, len, { grain: 'z', skip: ['+y'] });
  const beamZ = [zA, zbLast];
  for (let p = 0; p < room.pairs - 1; p++) beamZ.push(zA - p * PITCH - ALC_W - PART_T / 2);
  for (const z of beamZ) B.mahog.box(0, CEIL - 0.17, z, OUT_X * 2, 0.34, 0.3, { grain: 'x', skip: ['+y'] });
  for (const s of [-1, 1]) {
    B.walnut.box(s * (OUT_X + 0.2), CEIL / 2, (z0 + z1) / 2, 0.4, CEIL, len, { grain: 'y', skip: ['+y', '-y'] });
    const w = s < 0 ? makeWall(-OUT_X, z0, 1, 0, len) : makeWall(OUT_X, z1, -1, 0, len);
    bookshelves(B, w, 0, len, PART_H + 0.14, CEIL - 0.42, 0.3, 4);
    wbox(B.mahog, w, 0, len, CEIL - 0.42, CEIL, 0, 0.14, { grain: 'u' });
    wbox(B.mahog, w, 0, len, PART_H + 0.06, PART_H + 0.14, 0, 0.36, { grain: 'u' });
    const a = vec(w, len / 2, PART_H + 0.55, 0.42);
    B.brass.box(a.x, a.y, a.z, 0.03, 0.03, len, {});
  }
  const bodies = [[zA, z0], [z1, zbLast]];
  for (let p = 0; p < room.pairs - 1; p++) { const zb = zA - p * PITCH - ALC_W; bodies.push([zb - PART_T, zb]); }
  for (const s of [-1, 1]) {
    for (const [za, zb] of bodies) {
      B.walnut.box(s * (HALF + OUT_X) / 2, PART_H / 2, (za + zb) / 2, ALC_D, PART_H, zb - za, { grain: 'y', skip: ['-y'] });
      B.mahog.box(s * (HALF + OUT_X) / 2 - s * 0.05, PART_H + 0.06, (za + zb) / 2, ALC_D + 0.1, 0.12, zb - za + 0.12, { grain: 'x' });
    }
    B.mahog.box(s * (HALF + 0.08), PART_H + 0.06, (z0 + z1) / 2, 0.36, 0.12, len, { grain: 'z' });
    for (let p = 0; p < room.pairs - 1; p++) { const zb = zA - p * PITCH - ALC_W; pilaster(B, s * HALF, zb - PART_T / 2, -s, PART_T + 0.14); }
    pilaster(B, s * HALF, zA + 0.2, -s, 0.36); pilaster(B, s * HALF, zbLast - 0.2, -s, 0.36);
    for (let p = 0; p < room.pairs; p++) {
      const zt = zA - p * PITCH, zb = zt - ALC_W;
      B.mahog.box(s * (HALF + 0.09), 4.27, (zt + zb) / 2, 0.26, 0.46, ALC_W, { grain: 'z' });
    }
    for (const [za, zb] of [[zA, z0], [z1, zbLast]]) {
      const w = s < 0 ? makeWall(-HALF, zb, 1, 0, zb - za) : makeWall(HALF, za, -1, 0, zb - za);
      decorateWall(B, w, 4.04, { noCornice: true });
    }
  }
  for (const alc of room.alcoves) {
    for (const w of alc.walls) decorateWall(B, w, PART_H, { noCornice: true });
    const s = alc.side, zc = (alc.zt + alc.zb) / 2, first = alc.pieces[0], last = alc.pieces[alc.pieces.length - 1];
    const tex = plaque(768, 128, (c, W, H) => {
      if (first && room.early) { const a = eraOf(first), b = eraOf(last); engrave(c, a === b ? a : `${a} – ${b}`, W / 2, H * 0.52, `italic 54px "IM Fell English", Georgia, serif`); }
      else if (first) engrave(c, `Nos. ${first.n} – ${last.n}`, W / 2, H * 0.52, `56px "Special Elite", "Courier New", monospace`);
      else engrave(c, 'The reading nook', W / 2, H * 0.52, `italic 58px "IM Fell English", Georgia, serif`);
    });
    const pm = plaqueMesh(tex, 1.5, 0.25);
    pm.position.set(s * (HALF - 0.045), 4.27, zc); pm.rotation.y = s < 0 ? Math.PI / 2 : -Math.PI / 2;
    group.add(pm);
    if (!first) buildNook(B, group, glows, room, alc);
  }
  flushChunks(group, hangArt(B, glows, room.walls));
  rugQuad(B.rug, 0, (zA + zbLast) / 2, 2.3, zA - zbLast + 1.0);
  for (let p = 0; p < room.pairs; p++) {
    const zc = zA - p * PITCH - ALC_W / 2;
    addChandelier(group, glows, room, 0, zc, 1, 4.5);
    if (p % 2 === 0) { place(group, MODELS.bench, 0, 0, zc, Math.PI / 2); contactShadow(B.contact, 0, zc, 0.9, 2.0); room.colliders.push([-0.36, 0.36, zc - 0.9, zc + 0.9]); }
  }
  const ent = makeWall(-HALF, z0, 1, 0, PAD);
  plaqueOn(group, ent, PAD / 2, 1.62, wingPlaque(room), 1.5, 1.055, 'brass', 0.022);
  const endTex = plaque(1024, 520, (c, W, H) => {
    engrave(c, `END OF ${room.title.toUpperCase()}`, W / 2, H * 0.24, `${room.title.length > 18 ? 40 : 50}px "IM Fell English SC", Georgia, serif`);
    engrave(c, 'Back through the far door:', W / 2, H * 0.48, `italic 54px "IM Fell English", Georgia, serif`);
    engrave(c, room.back, W / 2, H * 0.7, `italic 74px "IM Fell English", Georgia, serif`);
  });
  plaqueOn(group, makeWall(-HALF, zbLast, 1, 0, PAD), PAD / 2, 1.6, endTex, 1.3, 0.66, 'brass', 0.022);
  for (const [zz, flip] of [[z0 - 1.3, 1], [z1 + 1.3, -1]]) {
    const cz = zz + flip * 0.95;
    const cand = place(group, MODELS.candelabrum, -1.55, 0, cz); addFlames(glows, cand.flames, 1);
    contactShadow(B.contact, -1.55, cz, 0.5, 0.5); room.colliders.push([-1.75, -1.35, cz - 0.2, cz + 0.2]);
    room.lights.push({ pos: cand.flames[1].clone(), color: 0xffa860, power: 3, flicker: 0.12 });
    place(group, MODELS.chair, 1.95, 0, zz, -Math.PI / 2 - flip * 0.35); contactShadow(B.contact, 1.95, zz, 1.1, 1.1);
    const tb = place(group, MODELS.sideTable, 2.0, 0, zz + flip * 0.85); addFlames(glows, tb.flames, 0.8); contactShadow(B.contact, 2.0, zz + flip * 0.85, 0.7, 0.7);
    room.colliders.push([1.45, HALF, zz - 0.5, zz + 0.5], [1.7, 2.3, zz + flip * 0.85 - 0.3, zz + flip * 0.85 + 0.3]);
    room.lights.push({ pos: tb.flames[0].clone(), color: 0xffa860, power: 1.6, flicker: 0.15 });
  }
  buildDivider(B, group, { small: 'BACK TO', big: room.back }, { small: room.early ? 'BEFORE THE STAMPS' : 'ENTER', big: room.title });
  B.walnut.box(0, CEIL / 2, z1 - WALL_T / 2, OUT_X * 2 + 0.7, CEIL, WALL_T, { grain: 'y' });
  const endW = makeWall(-HALF, z1, 0, 1, AISLE);
  decorateWall(B, endW, PART_H, { noCornice: true });
  upperPanels(B, makeWall(-OUT_X, z1, 0, 1, OUT_X * 2), PART_H, CEIL);
  bookshelves(B, endW, 0.5, AISLE - 0.5, 0.62, 3.9, 0.34, 7);
  flushBufs(group, B); flushGlows(group, glows);
  return group;
}
function buildNook(B, group, glows, room, alc) {
  const s = alc.side, zc = (alc.zt + alc.zb) / 2;
  bookshelves(B, alc.walls[1], 0.25, ALC_W - 0.25, 0.62, 3.9, 0.34, 7);
  rugQuad(B.rugWide, s * (HALF + ALC_D / 2), zc, 2.6, 3.4, true);
  const x = s * (HALF + 2.0);
  for (const dz of [-1.05, 1.05]) { place(group, MODELS.chair, x, 0, zc + dz, dz < 0 ? 0 : Math.PI); contactShadow(B.contact, x, zc + dz, 1.1, 1.1); room.colliders.push([x - 0.5, x + 0.5, zc + dz - 0.5, zc + dz + 0.5]); }
  const tb = place(group, MODELS.sideTable, x, 0, zc); addFlames(glows, tb.flames, 0.8); contactShadow(B.contact, x, zc, 0.7, 0.7);
  room.colliders.push([x - 0.32, x + 0.32, zc - 0.32, zc + 0.32]);
  room.lights.push({ pos: tb.flames[0].clone(), color: 0xffa860, power: 1.8, flicker: 0.15 });
}

function buildFireplace(B, group, glows, room, w, uc) {
  wbox(B.iron, w, uc - 1.25, uc + 1.25, 0, 0.07, 0, 0.75, {});
  wbox(B.iron, w, uc - 0.72, uc + 0.72, 0.07, 1.16, 0, 0.05, {});
  for (const sgn of [-1, 1]) {
    wbox(B.iron, w, uc + sgn * 0.72 - 0.03, uc + sgn * 0.72 + 0.03, 0.07, 1.16, 0, 0.42, {});
    wbox(B.mahog, w, uc + sgn * 0.93 - 0.21, uc + sgn * 0.93 + 0.21, 0, 1.36, 0, 0.44, { grain: 'y' });
    wbox(B.fluted, w, uc + sgn * 0.93 - 0.14, uc + sgn * 0.93 + 0.14, 0.16, 1.2, 0.44, 0.47, { grain: 'y', scale: [0.28, 1.2], ou: 0, ov: 0 });
  }
  wbox(B.iron, w, uc - 0.72, uc + 0.72, 1.12, 1.16, 0, 0.42, {});
  wbox(B.mahog, w, uc - 1.14, uc + 1.14, 1.16, 1.42, 0, 0.44, { grain: 'u' });
  wbox(B.burl, w, uc - 0.6, uc + 0.6, 1.2, 1.38, 0.44, 0.455, { grain: 'u' });
  wbox(B.mahog, w, uc - 1.3, uc + 1.3, 1.42, 1.5, 0, 0.54, { grain: 'u' });
  for (const [du, y, n] of [[-0.18, 0.15, 0.22], [0.16, 0.15, 0.2], [0, 0.27, 0.24]]) {
    const lg = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.08, 0.95, 10), M.iron);
    lg.position.copy(vec(w, uc + du, y, n)); lg.rotation.set(0, Math.PI / 2 + du * 0.6, Math.PI / 2); group.add(lg);
  }
  for (const sgn of [-1, 1]) { const p = vec(w, uc + sgn * 0.45, 0.2, 0.4); B.brass.box(p.x, p.y, p.z, 0.06, 0.4, 0.06); const q = vec(w, uc + sgn * 0.45, 0.42, 0.4); B.brass.addGeometry(new THREE.SphereGeometry(0.05, 12, 8), new THREE.Matrix4().makeTranslation(q.x, q.y, q.z)); }
  const fr = rng(4);
  for (let i = 0; i < 16; i++) { const p = vec(w, uc + (fr() - 0.5) * 0.9, 0.26 + fr() * 0.34, 0.22 + fr() * 0.12); glows.push([p.x, p.y, p.z, 0.18 + fr() * 0.26, 'flame']); }
  for (let i = 0; i < 4; i++) { const p = vec(w, uc + (i - 1.5) * 0.25, 0.4, 0.3); glows.push([p.x, p.y, p.z, 1.4, 'halo']); }
  const ember = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 1.6), M.ember);
  ember.position.copy(vec(w, uc, 0.012, 1.0)); ember.rotation.x = -Math.PI / 2; ember.rotation.z = Math.atan2(w.U[0], w.U[1]) - Math.PI / 2; ember.renderOrder = 1; group.add(ember);
  room.lights.push({ pos: vec(w, uc, 0.6, 0.9), color: 0xff8a3c, power: 26, flicker: 0.35, fire: true });
  room.fireL = vec(w, uc, 0.5, 0.3);
}
function buildSalon(room) {
  const group = new THREE.Group(), B = newBufs(), glows = [];
  const W = SWH, L = room.len, uc = FIRE_U, C = room.ceil;
  upQuad(B.floor, -W, W, -L, 0, 0, [1.6, 1.6]);
  ceilQuad(B.ceil, -W, W, 0, -L, C);
  for (let i = 1; i < 6; i++) B.mahog.box(-W + i * (W * 2 / 6), C - 0.17, -L / 2, 0.3, 0.34, L, { grain: 'z', skip: ['+y'] });
  const nb = Math.round(L / 3.2);
  for (let i = 1; i < nb; i++) B.mahog.box(0, C - 0.17, -i * L / nb, W * 2, 0.34, 0.3, { grain: 'x', skip: ['+y'] });
  // wall bodies: the far wall carries a centred door, the back wall too when Early Work is hung
  for (const [z0, z1] of room.backDoor ? [[0, 0.4], [-L - 0.4, -L]] : [[-L - 0.4, -L]]) {
    for (const s of [-1, 1]) B.walnut.box(s * (W + 0.4 + DOOR_W / 2) / 2, C / 2, (z0 + z1) / 2, W + 0.4 - DOOR_W / 2, C, 0.4, { grain: 'y' });
    B.walnut.box(0, (DOOR_H + C) / 2, (z0 + z1) / 2, DOOR_W, C - DOOR_H, 0.4, { grain: 'x' });
    doorLining(B, 0, (z0 + z1) / 2, true, 0.4);
  }
  if (!room.backDoor) B.walnut.box(0, C / 2, 0.2, (W + 0.4) * 2, C, 0.4, { grain: 'y' });
  for (const s of [-1, 1]) B.walnut.box(s * (W + 0.2), C / 2, -L / 2, 0.4, C, L, { grain: 'y' });
  // panelling (low wainscot) and the library shelves above the hang
  const runs = [makeWall(-W, 0, 1, 0, L), makeWall(W, -L, -1, 0, L), makeWall(-W, -L, 0, 1, W - 1.42), makeWall(1.42, -L, 0, 1, W - 1.42), makeWall(W, 0, 0, -1, W - 1.42), makeWall(-1.42, 0, 0, -1, W - 1.42)];
  if (!room.backDoor) runs.push(makeWall(1.42, 0, 0, -1, 2.84)); // panel over the closed doorway
  for (const w of runs) { decorateWall(B, w, C, { fieldTop: 3.92, noCornice: true }); bookshelves(B, w, 0.15, w.len - 0.15, 4.12, C - 0.42, 0.3, 4); wbox(B.mahog, w, 0, w.len, C - 0.42, C, 0, 0.14, { grain: 'u' }); }
  doorCasing(B, group, makeWall(0, -L, 0, 1, 0), { small: 'STRAIGHT AHEAD', big: 'The Long Gallery' });
  if (room.backDoor) doorCasing(B, group, makeWall(0, 0, 0, -1, 0), { small: 'BEFORE THE STAMPS', big: 'Early Work' });
  // the fireplace; its overmantel carries the first drawings
  const left = runs[0];
  buildFireplace(B, group, glows, room, left, uc);
  wbox(B.burl, left, uc - 1.25, uc + 1.25, 1.5, 3.5, FIELD_N - 0.002, FIELD_N + 0.008, { grain: 'y', scale: [1.2, 2.2] });
  flushChunks(group, hangArt(B, glows, room.walls));
  // welcome plaque and directory board flank the Early Work door
  const back = runs[4];
  const total = world.numbered.length, ec = world.early.length;
  const wt = plaque(1024, 1180, (c, Wd, H) => {
    engrave(c, 'SUPER EXTRA FANCY', Wd / 2, H * 0.1, `64px "IM Fell English SC", Georgia, serif`);
    engrave(c, 'The Museum', Wd / 2, H * 0.24, `italic 150px "IM Fell English", Georgia, serif`);
    c.fillStyle = 'rgba(43,26,11,.6)'; c.fillRect(Wd * 0.3, H * 0.33, Wd * 0.4, 3);
    c.font = `42px "Spectral", Georgia, serif`;
    const body = `Every numbered drawing by Cole Skaggs, ${fmt(total)} of them, hung at true size in the order each was stamped. This room holds the first thousand, starting over the mantel.`;
    wrapLines(c, body, Wd * 0.8).forEach((l, i) => engrave(c, l, Wd / 2, H * (0.41 + i * 0.056), c.font));
    c.font = `italic 44px "IM Fell English", Georgia, serif`;
    wrapLines(c, `Ahead, the Long Gallery runs to this week, with the Small, Medium and Large galleries off its side doors.${ec ? ' Behind you, the early work.' : ''}`, Wd * 0.8)
      .forEach((l, i) => engrave(c, l, Wd / 2, H * (0.71 + i * 0.06), c.font));
  });
  const bu = W - 2.95; // back-wall u for x = +2.95
  wbox(B.mahog, back, bu - 0.92, bu + 0.92, 1.62 - 1.06, 1.62 + 1.06, 0, 0.025, { grain: 'y' });
  plaqueOn(group, back, bu, 1.62, wt, 1.62, 1.87, 'brass', 0.03);
  const dt = plaque(900, 1100, (c, Wd, H) => {
    engrave(c, 'DIRECTORY', Wd / 2, H * 0.08, `60px "IM Fell English SC", Georgia, serif`, 'center', '#e9dcc4');
    c.fillStyle = 'rgba(233,220,196,.4)'; c.fillRect(Wd * 0.12, H * 0.13, Wd * 0.76, 2);
    const rows = world.rooms.filter((r) => r.kind !== 'vest');
    rows.forEach((r, i) => {
      const y = H * (0.21 + i * (0.72 / rows.length));
      engrave(c, r.title.replace(/^The /, ''), Wd * 0.1, y, `italic 50px "IM Fell English", Georgia, serif`, 'left', '#f1e6cf');
      engrave(c, r.rangeShort, Wd * 0.92, y, `34px "Special Elite", monospace`, 'right', '#c9a35f');
    });
  }, 'felt');
  const du = W + 2.95;
  wbox(B.mahog, back, du - 0.68, du + 0.68, 1.62 - 0.81, 1.62 + 0.81, 0, 0.035, { grain: 'y' });
  plaqueOn(group, back, du, 1.62, dt, 1.2, 1.466, 'felt', 0.04);
  addLamps(B, { ...back, lamps: [bu, du], lampY: 2.75 }, glows);
  // furniture: chairs at the fire, a library table, benches down the middle
  rugQuad(B.rugWide, -W + 3.0, -uc, 3.6, 4.8, true);
  for (const z of [-uc + 1.4, -uc - 1.4]) {
    const x = -W + 3.1, ang = Math.atan2(-W + 0.6 - x, -uc - z);
    place(group, MODELS.chair, x, 0, z, ang); contactShadow(B.contact, x, z, 1.15, 1.15); room.colliders.push([x - 0.5, x + 0.5, z - 0.5, z + 0.5]);
  }
  { const x = -W + 3.45, tb = place(group, MODELS.sideTable, x, 0, -uc); addFlames(glows, tb.flames, 0.8); contactShadow(B.contact, x, -uc, 0.7, 0.7); room.colliders.push([x - 0.3, x + 0.3, -uc - 0.3, -uc + 0.3]); room.lights.push({ pos: tb.flames[0].clone(), color: 0xffa860, power: 1.6, flicker: 0.15 }); }
  { const x = 3.4, z = -L * 0.42, lt = place(group, MODELS.libTable, x, 0, z, Math.PI / 2); addFlames(glows, lt.flames, 0.9); contactShadow(B.contact, x, z, 1.3, 2.2); room.colliders.push([x - 0.5, x + 0.5, z - 1.0, z + 1.0]); room.lights.push({ pos: lt.flames[1].clone(), color: 0xffa860, power: 4, flicker: 0.12 });
    for (const dz of [-1.35, 1.35]) { place(group, MODELS.chair, x, 0, z + dz * 1.1, dz < 0 ? 0 : Math.PI, 0.95); contactShadow(B.contact, x, z + dz * 1.1, 1.0, 1.0); room.colliders.push([x - 0.5, x + 0.5, z + dz * 1.1 - 0.45, z + dz * 1.1 + 0.45]); } }
  for (const f of [0.62, 0.82]) { const z = -L * f; place(group, MODELS.bench, 0, 0, z, Math.PI / 2); contactShadow(B.contact, 0, z, 0.9, 2.0); room.colliders.push([-0.36, 0.36, z - 0.9, z + 0.9]); }
  rugQuad(B.rug, 0, -L * 0.72, 2.3, L * 0.42);
  for (const x of [-2.1, 2.1]) { const z = -L + 0.6, c = place(group, MODELS.candelabrum, x, 0, z); addFlames(glows, c.flames, 1); contactShadow(B.contact, x, z, 0.5, 0.5); room.colliders.push([x - 0.2, x + 0.2, z - 0.2, z + 0.2]); room.lights.push({ pos: c.flames[1].clone(), color: 0xffa860, power: 3, flicker: 0.12 }); }
  addChandelier(group, glows, room, 0, -uc, 1.45, 4.2);
  for (let z = -uc - 7; z > -L + 3; z -= 7) addChandelier(group, glows, room, 0, z, 1.3, 4.3);
  flushBufs(group, B); flushGlows(group, glows);
  return group;
}
function buildVestibule(room) {
  const group = new THREE.Group(), B = newBufs(), glows = [], C = room.ceil, L = VEST_L;
  upQuad(B.floor, -VEST_W, VEST_W, -L, 0, 0, [1.6, 1.6]);
  ceilQuad(B.ceil, -VEST_W, VEST_W, 0, -L, C);
  B.mahog.box(0, C - 0.12, -L / 2, VEST_W * 2, 0.24, 0.3, { grain: 'x', skip: ['+y'] });
  for (const s of [-1, 1]) {
    B.walnut.box(s * (VEST_W + 0.2), C / 2, -L / 2, 0.4, C, L + 0.02, { grain: 'y' });
    const w = s < 0 ? makeWall(-VEST_W, 0, 1, 0, L) : makeWall(VEST_W, -L, -1, 0, L);
    decorateWall(B, w, C, { fieldTop: 2.95 });
    // a brass sconce with a candle
    const p = vec(w, L / 2, 1.85, 0.1);
    B.brass.box(p.x, p.y - 0.05, p.z, 0.09, 0.02, 0.09);
    wbox(B.brass, w, L / 2 - 0.05, L / 2 + 0.05, 1.7, 1.86, FIELD_N, FIELD_N + 0.03);
    const cw = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.019, 0.16, 10), M.wax); cw.position.set(p.x, p.y + 0.04, p.z); group.add(cw);
    const f = new THREE.Vector3(p.x, p.y + 0.15, p.z); addFlames(glows, [f], 0.9);
    room.lights.push({ pos: f, color: 0xffa860, power: 1.5, flicker: 0.15 });
  }
  rugQuad(B.rug, 0, -L / 2, 1.4, L - 0.3);
  flushBufs(group, B); flushGlows(group, glows);
  return group;
}
function buildLong(room) {
  const group = new THREE.Group(), B = newBufs(), glows = [], C = room.ceil, L = room.len, X = LH;
  const bayTop = (b) => -(L_ENTRY + PIER + b * (BAY + PIER));
  upQuad(B.floor, -X, X, -L, 0, 0, [1.6, 1.6]);
  ceilQuad(B.ceil, -X, X, 0, -L, C);
  B.mahog.box(0, C - 0.14, -L / 2, 0.3, 0.28, L, { grain: 'z', skip: ['+y'] });
  // entry and end walls
  for (const s of [-1, 1]) B.walnut.box(s * (X + LW_T + DOOR_W / 2) / 2, C / 2, 0.2, X + LW_T - DOOR_W / 2, C, 0.4, { grain: 'y' });
  B.walnut.box(0, (DOOR_H + C) / 2, 0.2, DOOR_W, C - DOOR_H, 0.4, { grain: 'x' });
  doorLining(B, 0, 0.2, true, 0.4);
  for (const [x0, x1] of [[-X, -DOOR_W / 2 - 0.22], [DOOR_W / 2 + 0.22, X]]) decorateWall(B, makeWall(x1, 0, 0, -1, x1 - x0), C, { fieldTop: 2.95 });
  doorCasing(B, group, makeWall(0, 0, 0, -1, 0), null);
  B.walnut.box(0, C / 2, -L - 0.2, (X + LW_T) * 2, C, 0.4, { grain: 'y' });
  const endW = makeWall(-X, -L, 0, 1, X * 2);
  decorateWall(B, endW, C, { fieldTop: 2.95 });
  bookshelves(B, endW, 0.5, X * 2 - 0.5, 0.62, 3.6, 0.34, 6);
  // side walls: bodies, panelling and doors into the wings
  const doors = [...room.doors].sort((a, b) => b.z - a.z);
  B.walnut.box(-X - LW_T / 2, C / 2, -L / 2, LW_T, C, L, { grain: 'y' });
  decorateWall(B, makeWall(-X, 0, 1, 0, L), C, { fieldTop: 2.95 });
  let zTop = 0;
  const rightRuns = [];
  for (const d of doors) { rightRuns.push([d.z + DOOR_W / 2, zTop]); zTop = d.z - DOOR_W / 2; }
  rightRuns.push([-L, zTop]);
  for (const [za, zb] of rightRuns) {
    B.walnut.box(X + LW_T / 2, C / 2, (za + zb) / 2, LW_T, C, zb - za, { grain: 'y' });
    const segA = za + (za > -L + 0.01 ? 0.22 : 0), segB = zb - (zb < -0.01 ? 0.22 : 0);
    decorateWall(B, makeWall(X, segA, -1, 0, segB - segA), C, { fieldTop: 2.95 });
  }
  const LD_H = 3.0;
  for (const d of doors) {
    B.walnut.box(X + LW_T / 2, (LD_H + C) / 2, d.z, LW_T, C - LD_H, DOOR_W, { grain: 'z' });
    doorLining(B, X + LW_T / 2, d.z, false, LW_T, DOOR_W, LD_H);
    const wc = makeWall(X, d.z, -1, 0, 0);
    const title = d.room ? d.room.title : '';
    doorCasing(B, group, wc, { small: 'THROUGH HERE', big: title.replace(/^The /, '') }, DOOR_W, LD_H);
  }
  // piers with pilasters, beams, year signs
  const nPiers = room.bays + 1;
  for (let i = 0; i < nPiers; i++) {
    const zc = bayTop(i) + PIER / 2;
    for (const s of [-1, 1]) if (!(s > 0 && doors.some((d) => Math.abs(d.z - zc) < DOOR_W / 2 + 0.3))) pilaster(B, s * X, zc, -s, PIER, 0, 3.25);
    B.mahog.box(0, C - 0.17, zc, X * 2, 0.34, 0.34, { grain: 'x', skip: ['+y'] });
  }
  for (const y of room.years) {
    const tex = plaque(768, 200, (c, Wd, H) => {
      engrave(c, String(y.year), Wd / 2, H * 0.42, `italic 120px "IM Fell English", Georgia, serif`);
      engrave(c, `from No. ${y.piece.n}`, Wd / 2, H * 0.82, `38px "Special Elite", monospace`);
    });
    for (const face of [1, -1]) {
      const pm = plaqueMesh(tex, 1.15, 0.3); pm.position.set(0, 3.62, y.z + face * 0.012); pm.rotation.y = face > 0 ? 0 : Math.PI; group.add(pm);
    }
    for (const sx of [-0.45, 0.45]) B.brass.box(sx, (3.77 + C - 0.34) / 2, y.z, 0.012, C - 0.34 - 3.77, 0.012);
    B.brass.box(0, 3.62, y.z, 1.2, 0.33, 0.012);
  }
  // the hang: lamps and drawings on every bay
  flushChunks(group, hangArt(B, glows, room.walls.filter((w) => w.door == null)));
  // entry plaque, furnishings
  const entTex = plaque(1024, 720, (c, Wd, H) => {
    engrave(c, 'THE LONG GALLERY', Wd / 2, H * 0.14, `54px "IM Fell English SC", Georgia, serif`);
    engrave(c, 'Extra Small', Wd / 2, H * 0.37, `italic 150px "IM Fell English", Georgia, serif`);
    engrave(c, '5 × 8.5 in sheets, four rows deep', Wd / 2, H * 0.56, `italic 48px "IM Fell English", Georgia, serif`);
    engrave(c, room.range, Wd / 2, H * 0.7, `52px "Special Elite", "Courier New", monospace`);
    engrave(c, `${fmt(room.list.length)} drawings, oldest to newest`, Wd / 2, H * 0.84, `italic 46px "IM Fell English", Georgia, serif`);
  });
  plaqueOn(group, makeWall(-X, 0, 1, 0, L_ENTRY), 1.3, 1.6, entTex, 1.5, 1.055, 'brass', 0.022);
  for (let b = 0; b < room.bays; b++) {
    const zc = bayTop(b) - BAY / 2;
    if (b % 2 === 0) addChandelier(group, glows, room, 0, zc, 0.72, 3.3, C);
    if (b % 3 === 1) { place(group, MODELS.bench, 0, 0, zc, Math.PI / 2); contactShadow(B.contact, 0, zc, 0.9, 2.0); room.colliders.push([-0.36, 0.36, zc - 0.9, zc + 0.9]); }
    if (b % 2 === 0) rugQuad(B.rug, 0, zc - (BAY + PIER) / 2, 1.9, (BAY + PIER) * 2 - 0.6);
  }
  for (const x of [-2.2, 2.2]) { const z = -1.0, c = place(group, MODELS.candelabrum, x, 0, z); addFlames(glows, c.flames, 1); contactShadow(B.contact, x, z, 0.5, 0.5); room.colliders.push([x - 0.2, x + 0.2, z - 0.2, z + 0.2]); room.lights.push({ pos: c.flames[1].clone(), color: 0xffa860, power: 3, flicker: 0.12 }); }
  const endTex = plaque(1024, 520, (c, Wd, H) => {
    engrave(c, 'THE NEWEST WORK', Wd / 2, H * 0.2, `50px "IM Fell English SC", Georgia, serif`);
    engrave(c, `Last stamped here: No. ${room.list[room.list.length - 1].n}`, Wd / 2, H * 0.45, `48px "Special Elite", "Courier New", monospace`);
    c.font = `italic 44px "IM Fell English", Georgia, serif`;
    wrapLines(c, 'Cole draws about 28 a week, so this gallery keeps growing. To be continued.', Wd * 0.8).forEach((l, i) => engrave(c, l, Wd / 2, H * (0.66 + i * 0.12), c.font));
  });
  plaqueOn(group, makeWall(-X, -L + L_END, 1, 0, L_END), L_END / 2, 1.6, endTex, 1.3, 0.66, 'brass', 0.022);
  { const z = -L + 1.2; place(group, MODELS.chair, 1.6, 0, z, -Math.PI / 2 - 0.5); contactShadow(B.contact, 1.6, z, 1.1, 1.1); room.colliders.push([1.1, 2.1, z - 0.5, z + 0.5]);
    const tb = place(group, MODELS.sideTable, 2.45, 0, z + 0.9); addFlames(glows, tb.flames, 0.8); contactShadow(B.contact, 2.45, z + 0.9, 0.7, 0.7); room.colliders.push([2.15, 2.75, z + 0.6, z + 1.2]); room.lights.push({ pos: tb.flames[0].clone(), color: 0xffa860, power: 1.8, flicker: 0.15 }); }
  flushBufs(group, B); flushGlows(group, glows);
  return group;
}

/* ------------------------------------------------------------------ texture streaming */
const loader = new THREE.TextureLoader();
const imageCache = new Map();
function loadTexture(url, channel = 0) {
  return new Promise((res, rej) => loader.load(url, (t) => {
    t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = Math.min(IS_TOUCH ? 2 : 4, MAX_ANISO); t.channel = channel;
    imageCache.set(url, t.image); res(t);
  }, undefined, rej));
}
function applyChunkMaterial(ch) {
  if (ch.mat && ch.state === 'ready') { if (ch.mesh) ch.mesh.material = ch.mat; return; }
  const lo = world.loAtlases[ch.lo];
  if (ch.mesh) ch.mesh.material = lo && lo.mat ? lo.mat : M.artPending;
}
let loadingHi = 0;
async function loadHi(ch) {
  ch.state = 'loading'; loadingHi++;
  try {
    const t = await loadTexture(ASSET('art/' + ch.url));
    if (ch.state !== 'loading') { t.dispose(); return; }
    ch.tex = t; ch.mat = new THREE.MeshBasicMaterial({ map: t, vertexColors: true, toneMapped: false }); ch.state = 'ready';
    applyChunkMaterial(ch);
  } catch (e) { ch.state = 'none'; console.warn('hi atlas failed', ch.url, e); }
  finally { loadingHi--; }
}
function unloadHi(ch) {
  if (ch.mat) ch.mat.dispose(); if (ch.tex) ch.tex.dispose();
  ch.mat = ch.tex = null; ch.state = 'none'; applyChunkMaterial(ch);
}
async function loadLo(idx) {
  const lo = world.loAtlases[idx]; if (lo.state !== 'none') return;
  lo.state = 'loading';
  try {
    const t = await loadTexture(ASSET('art/' + lo.url), 1);
    if (lo.state !== 'loading') { t.dispose(); return; }
    lo.tex = t; lo.mat = new THREE.MeshBasicMaterial({ map: t, vertexColors: true, toneMapped: false }); lo.state = 'ready';
    for (const ch of world.chunks) if (ch.lo === idx) applyChunkMaterial(ch);
  } catch (e) { lo.state = 'none'; console.warn('lo atlas failed', lo.url, e); }
}
function unloadLo(idx) {
  const lo = world.loAtlases[idx];
  if (lo.mat) lo.mat.dispose(); if (lo.tex) lo.tex.dispose();
  lo.mat = lo.tex = null; lo.state = 'none';
  for (const ch of world.chunks) if (ch.lo === idx) applyChunkMaterial(ch);
}
function updateStreaming() {
  const pos = camera.position, cand = [], loWanted = new Set();
  for (const ch of world.chunks) {
    if (!ch.room) continue;
    ch.dist = ch.box.distanceToPoint(pos);
    if (ch.group) ch.group.visible = ch.dist < 48;
    if (ch.dist < 60) loWanted.add(ch.lo);
    if (ch.room.near < 42) cand.push(ch); else if (ch.state === 'ready') unloadHi(ch);
  }
  world.loAtlases.forEach((lo, i) => { if (loWanted.has(i)) loadLo(i); else if (lo.state === 'ready') unloadLo(i); });
  cand.sort((a, b) => a.dist - b.dist);
  const want = cand.filter((c) => c.dist < Q.hiRadius).slice(0, Q.hiMax);
  const ready = cand.filter((c) => c.state === 'ready').length;
  for (const ch of cand) if (ch.state === 'ready' && !want.includes(ch) && (ch.dist > Q.hiRadius + 3 || ready > Q.hiMax + 2)) unloadHi(ch);
  for (const ch of want) if (ch.state === 'none' && loadingHi < 3) loadHi(ch);
}

/* ------------------------------------------------------------------ state, movement, input */
const state = { room: 0, yaw: 0, pitch: 0, vel: new THREE.Vector3(), keys: new Set(), locked: false, dragLook: false, overlay: 'intro', focus: null, joy: [0, 0], started: false, inspect: null, lastTick: 0 };
const lightPool = [];
function setupLights() {
  for (let i = 0; i < Q.lights; i++) { const l = new THREE.PointLight(0xffb46a, 0, 15, 2); scene.add(l); lightPool.push({ light: l, src: null }); }
}
function roomDist(r, x, z) {
  const b = r.boundsW, dx = Math.max(b[0] - x, 0, x - b[1]), dz = Math.max(b[2] - z, 0, z - b[3]);
  return Math.hypot(dx, dz);
}
function updateLights() {
  const pos = camera.position, cands = [];
  for (const r of world.rooms) if (roomDist(r, pos.x, pos.z) < 22) for (const s of r.lightsW) cands.push(s);
  cands.sort((a, b) => a.pos.distanceToSquared(pos) / a.power - b.pos.distanceToSquared(pos) / b.power);
  const chosen = cands.slice(0, lightPool.length);
  lightPool.forEach((slot, i) => {
    const s = chosen[i] || null; slot.src = s;
    if (s) { slot.light.position.copy(s.pos); slot.light.color.setHex(s.color); slot.light.distance = s.fire ? 12 : s.power > 10 ? 15 : 6; }
    else slot.light.intensity = 0;
  });
}
function flickerLights(t) {
  for (const [i, slot] of lightPool.entries()) {
    const s = slot.src; if (!s) continue;
    const f = REDUCED ? 1 : 1 - s.flicker * (0.5 + 0.5 * Math.sin(t * 7.1 + i * 1.7) * Math.sin(t * 13.3 + i * 0.7));
    slot.light.intensity = s.power * f;
  }
}
function roomAt(x, z) {
  for (const r of world.rooms) { const b = r.boundsW; if (x >= b[0] && x <= b[1] && z >= b[2] && z <= b[3]) return r.index; }
  return -1;
}
function collide(p) {
  const r = 0.3;
  for (let iter = 0; iter < 2; iter++) {
    for (const room of world.rooms) {
      if (roomDist(room, p.x, p.z) > 1.5) continue;
      for (const [x0, x1, z0, z1] of room.collW) {
        const cx = Math.max(x0, Math.min(p.x, x1)), cz = Math.max(z0, Math.min(p.z, z1));
        const dx = p.x - cx, dz = p.z - cz, d2 = dx * dx + dz * dz;
        if (d2 < r * r) {
          if (d2 > 1e-8) { const d = Math.sqrt(d2), k = (r - d) / d; p.x += dx * k; p.z += dz * k; }
          else { // centre inside the box: push out along the shallowest side
            const opts = [[p.x - x0 + r, -1, 0], [x1 - p.x + r, 1, 0], [p.z - z0 + r, 0, -1], [z1 - p.z + r, 0, 1]].sort((a, b) => a[0] - b[0]);
            p.x += opts[0][1] * opts[0][0]; p.z += opts[0][2] * opts[0][0];
          }
        }
      }
    }
  }
}
function updateMovement(dt) {
  const k = state.keys;
  let fx = 0, fz = 0;
  if (k.has('KeyW') || k.has('ArrowUp')) fz += 1;
  if (k.has('KeyS') || k.has('ArrowDown')) fz -= 1;
  if (k.has('KeyA')) fx -= 1;
  if (k.has('KeyD')) fx += 1;
  if (k.has('ArrowLeft')) state.yaw += dt * 1.8;
  if (k.has('ArrowRight')) state.yaw -= dt * 1.8;
  fx += state.joy[0]; fz += state.joy[1];
  const len = Math.hypot(fx, fz); if (len > 1) { fx /= len; fz /= len; }
  const speed = (k.has('ShiftLeft') || k.has('ShiftRight')) ? 5.2 : 2.0;
  const sy = Math.sin(state.yaw), cy = Math.cos(state.yaw);
  const tx = (-sy * fz + cy * fx) * speed, tz = (-cy * fz - sy * fx) * speed;
  const a = 1 - Math.exp(-dt * 9);
  state.vel.x += (tx - state.vel.x) * a; state.vel.z += (tz - state.vel.z) * a;
  const p = camera.position;
  const px = p.x, pz = p.z;
  p.x += state.vel.x * dt; p.z += state.vel.z * dt;
  collide(p);
  // never leave the building: if a step lands outside every room and every doorway, undo it
  if (roomAt(p.x, p.z) < 0 && !world.rooms.some((r) => roomDist(r, p.x, p.z) < 0.7)) { p.x = px; p.z = pz; }
  p.y = EYE;
  camera.rotation.set(state.pitch, state.yaw, 0, 'YXZ');
}
function updateRoomVisibility() {
  const { x, z } = camera.position, ri = roomAt(x, z);
  if (ri >= 0 && ri !== state.room) { state.room = ri; updateLights(); updateHUD(); }
  for (const r of world.rooms) {
    r.near = roomDist(r, x, z);
    const want = r.near < 42;
    if (want && !r.built) buildQueue.add(r.index);
    if (r.group) r.group.visible = want;
  }
}
const buildQueue = new Set();
function processBuildQueue() {
  if (!buildQueue.size) return;
  const idx = [...buildQueue].sort((a, b) => world.rooms[a].near - world.rooms[b].near)[0];
  buildQueue.delete(idx);
  buildRoom(world.rooms[idx]);
}
const BUILDERS = { salon: buildSalon, gallery: buildAlcoveGallery, long: buildLong, vest: buildVestibule };
function buildRoom(r) {
  if (r.built) return;
  const g = BUILDERS[r.kind](r);
  g.position.set(r.tf.ox, 0, r.tf.oz); g.rotation.y = r.tf.rot;
  r.group = g; r.built = true; scene.add(g);
  finalizeRoom(r); // picks up furniture colliders and lights added while building
}

/* focus: which drawing is under the crosshair (or a tapped point) */
const ray = new THREE.Raycaster(), ndc = new THREE.Vector2();
function pick(nx = 0, ny = 0, maxD = 4.6) {
  ndc.set(nx, ny); ray.setFromCamera(ndc, camera);
  const o = ray.ray.origin, d = ray.ray.direction;
  let best = null, bestT = maxD;
  for (const room of world.rooms) {
    if (room.near > maxD || !room.walls.length) continue;
    for (const w of room.walls) {
      const W = w.W, denom = d.x * W.N[0] + d.z * W.N[1]; if (denom > -1e-4) continue;
      const t = ((W.sx + W.N[0] * 0.04 - o.x) * W.N[0] + (W.sz + W.N[1] * 0.04 - o.z) * W.N[1]) / denom;
      if (t <= 0 || t >= bestT) continue;
      const hx = o.x + d.x * t, hy = o.y + d.y * t, hz = o.z + d.z * t;
      const u = (hx - W.sx) * W.U[0] + (hz - W.sz) * W.U[1];
      if (u < 0 || u > w.len || hy < 0 || hy > 4.6) continue;
      bestT = t; best = { w, u, y: hy };
    }
  }
  if (!best) return null;
  for (const p of best.w.pieces) if (Math.abs(best.u - p.u) <= p.ow / 2 + 0.01 && Math.abs(best.y - p.y) <= p.oh / 2 + 0.01) return p;
  return null;
}
function setFocus(p) {
  if (p === state.focus) return;
  state.focus = p;
  $('#crosshair').classList.toggle('on', !!p);
  const pl = $('#placard');
  if (!p || state.overlay) { pl.hidden = true; return; }
  $('#pl-no').textContent = stampOf(p);
  const lb = world.labels?.[p.n];
  $('#pl-meta').textContent = `${eraOf(p)} · ${SIZE_NAME[p.size]}${lb?.d ? ' · ' + lb.d.replace(/"/g, ' in').replace(/\s+in$/, ' in') : ''}`;
  $('#pl-act').textContent = IS_TOUCH ? 'Tap to look closer' : 'Click to look closer';
  pl.hidden = false;
}

/* teleport */
function fadeTo(fn) {
  const f = $('#fade');
  if (REDUCED) { fn(); return; }
  f.classList.add('on');
  setTimeout(() => { fn(); requestAnimationFrame(() => f.classList.remove('on')); }, 280);
}
function settle() { updateRoomVisibility(); while (buildQueue.size) processBuildQueue(); updateRoomVisibility(); updateStreaming(); updateLights(); updateHUD(); }
function standBefore(p) {
  const w = p.wall, dist = { XS: 1.0, S: 1.25, M: 1.6, L: 2.1, XL: 2.9 }[p.size] || 1.4;
  const pos = wallPointW(w, p.u, EYE, dist);
  camera.position.set(pos.x, EYE, pos.z);
  state.yaw = Math.atan2(w.W.N[0], w.W.N[1]);
  state.pitch = Math.atan2(p.y - EYE, dist) * 0.9;
  state.vel.set(0, 0, 0);
  settle();
}
function goTo(room, local) {
  const s = spawnOf(room, local);
  camera.position.set(s.x, EYE, s.z); state.yaw = s.yaw; state.pitch = 0; state.vel.set(0, 0, 0);
  settle();
}
const goToRoom = (r) => goTo(r);

/* HUD */
function longSection() {
  // which bay of the Long Gallery the camera is in, and what hangs there
  const long = world.long; if (!long || state.room !== long.index) return null;
  const lz = camera.position.z - long.tf.oz, bay = Math.floor((-lz - L_ENTRY - PIER) / (BAY + PIER));
  const ws = long.walls.filter((w) => w.bay === Math.max(0, Math.min(long.bays - 1, bay)) && w.pieces.length);
  const ps = ws.flatMap((w) => w.pieces); if (!ps.length) return null;
  ps.sort((a, b) => (a.n < b.n ? -1 : 1));
  return { key: ps[0].n, text: `${ps[ps.length - 1].year} · Nos. ${ps[0].n} – ${ps[ps.length - 1].n}` };
}
function updateHUD() {
  const r = world.rooms[state.room]; if (!r) return;
  $('#where-name').textContent = r.name;
  const sec = longSection();
  $('#where-sub').textContent = sec ? sec.text : r.sub;
  state.hudKey = sec?.key;
  if (!$('#directory').hidden) renderDirectory();
}
function renderDirectory() {
  const ol = $('#rooms'); ol.textContent = '';
  const row = (title, small, count, go, cls = '') => {
    const li = document.createElement('li'), b = document.createElement('button');
    b.type = 'button'; if (cls) li.className = cls;
    const rn = document.createElement('span'); rn.className = 'rn'; rn.textContent = cls.includes('sub') ? '' : '·';
    const rt = document.createElement('span'); rt.className = 'rt'; rt.textContent = title;
    const sm = document.createElement('small'); sm.textContent = small; rt.append(sm);
    const rc = document.createElement('span'); rc.className = 'rc'; rc.textContent = count;
    b.append(rn, rt, rc); b.addEventListener('click', () => { closeOverlay(); fadeTo(go); });
    li.append(b); ol.append(li);
  };
  for (const r of world.rooms) {
    if (r.kind === 'vest') continue;
    const here = r.index === state.room ? 'here' : '';
    const small = r.kind === 'salon' ? `${r.range} · the fireplace, where you came in` : r.early ? 'Early career, 2018–2020, Water Series, studies and zines' : r.kind === 'long' ? `Extra small · ${r.range}` : `${r.sizeLine} · ${r.range}`;
    row(r.title, small, fmt(r.list.length), () => goToRoom(r), here);
    if (r.kind === 'long') for (const y of r.years) row(`${y.year}`, `from No. ${y.piece.n}`, '', () => goTo(r, { x: 0, z: y.z - 0.8, yaw: 0 }), 'sub');
  }
}
function openDirectory() {
  if (state.overlay === 'inspect') return;
  releaseLock(); state.overlay = 'directory'; renderDirectory();
  $('#directory').hidden = false; setFocus(null); $('#find-msg').textContent = '';
  if (!IS_TOUCH) setTimeout(() => $('#find-no').focus(), 30);
}
function closeOverlay() {
  $('#directory').hidden = true; $('#inspect').hidden = true;
  const insp = state.inspect; state.inspect = null; state.overlay = null;
  showLookHint();
  return insp;
}
function showLookHint() {
  const h = $('#hint');
  if (IS_TOUCH || state.locked || state.overlay) { h.hidden = true; return; }
  h.textContent = state.dragLook ? 'Drag to look around' : 'Click to look around';
  h.hidden = false;
}
function findDrawing(raw) {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return { msg: 'Type the number stamped on the drawing, like 245 or 000245.' };
  const n = digits.padStart(6, '0').slice(-6);
  const p = world.byN.get(n);
  if (p) return { p };
  // numbers on a shared multi-sheet scan
  if (world.labels) for (const [k, v] of Object.entries(world.labels)) if (v.also && v.also.includes(n)) return { p: world.byN.get(k), note: `No. ${n} shares a scan with No. ${k}.` };
  const after = world.numbered.find((q) => q.n > n);
  return { msg: `No. ${n} isn't on the walls. Some numbers were never used.`, near: after };
}

/* inspect */
async function openInspect(p) {
  releaseLock(); state.overlay = 'inspect'; state.inspect = p; setFocus(null);
  const el = $('#inspect'); el.hidden = false;
  fillLabel(p);
  await drawInspect(p);
}
function fillLabel(p) {
  const lb = world.labels?.[p.n] || {};
  $('#lb-no').textContent = stampOf(p);
  $('#lb-title').textContent = lb.t || '';
  $('#lb-year').textContent = eraOf(p);
  $('#lb-size').textContent = `${SIZE_NAME[p.size]}${lb.d ? ', ' + lb.d.replace(/"/g, ' in').replace(/\s+in\s+in/, ' in') : ''}`;
  $('#lb-medium').textContent = lb.m || (!world.labels ? 'Loading…' : p.early ? 'Not catalogued yet' : '—');
  $('#lb-where').textContent = `${p.room.name}`;
  $('#lb-desc').textContent = lb.x || '';
  $('#lb-subj').textContent = lb.s ? lb.s.split(',').map((s) => s.trim()).filter(Boolean).join(' · ') : '';
  $('#lb-prev').disabled = p.i === 0; $('#lb-next').disabled = p.i === world.pieces.length - 1;
}
async function drawInspect(p) {
  const cv = $('#inspect-canvas'), ctx = cv.getContext('2d');
  const box = cv.parentElement.getBoundingClientRect();
  const narrow = innerWidth <= 760;
  const maxW = Math.max(160, (narrow ? innerWidth - 60 : box.width - 30)), maxH = Math.max(160, narrow ? innerHeight * 0.48 : innerHeight - 120);
  const s = Math.min(maxW / p.w, maxH / p.h), dw = Math.round(p.w * s), dh = Math.round(p.h * s), dpr = Math.min(2, devicePixelRatio || 1);
  cv.style.width = dw + 'px'; cv.style.height = dh + 'px'; cv.width = Math.round(dw * dpr); cv.height = Math.round(dh * dpr);
  ctx.fillStyle = '#2a1d13'; ctx.fillRect(0, 0, cv.width, cv.height);
  const url = ASSET('art/' + world.chunks[p.hi].url);
  let img = imageCache.get(url);
  if (!img) {
    // show the low-res version straight away
    const lo = world.loAtlases[p.lo];
    if (lo.tex?.image) { ctx.imageSmoothingQuality = 'high'; ctx.drawImage(lo.tex.image, p.lx, p.ly, p.lw, p.lh, 0, 0, cv.width, cv.height); }
    img = await new Promise((res) => { const im = new Image(); im.crossOrigin = 'anonymous'; im.onload = () => res(im); im.onerror = () => res(null); im.src = url; });
    if (img) imageCache.set(url, img);
  }
  if (state.inspect !== p || !img) return;
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, p.hx, p.hy, p.hw, p.hh, 0, 0, cv.width, cv.height);
}
function stepInspect(d) {
  const p = state.inspect; if (!p) return;
  const q = world.pieces[p.i + d]; if (!q) return;
  state.inspect = q; fillLabel(q); drawInspect(q);
}
function closeInspect() {
  const p = closeOverlay();
  if (p && p !== state.inspectStart && p.wall) fadeTo(() => standBefore(p));
}

/* pointer lock and mouse look */
function requestLock() {
  if (IS_TOUCH || state.dragLook) return;
  try {
    const r = canvas.requestPointerLock();
    if (r && r.catch) r.catch(() => { state.dragLook = true; showLookHint(); });
  } catch { state.dragLook = true; showLookHint(); }
}
function releaseLock() { if (document.pointerLockElement) document.exitPointerLock(); }
document.addEventListener('pointerlockchange', () => {
  state.locked = document.pointerLockElement === canvas;
  $('#crosshair').hidden = !state.locked && !IS_TOUCH ? true : false;
  if (IS_TOUCH) $('#crosshair').hidden = true;
  showLookHint();
});
document.addEventListener('pointerlockerror', () => { state.dragLook = true; showLookHint(); });
document.addEventListener('mousemove', (e) => {
  if (!state.locked) return;
  look(e.movementX, e.movementY, 0.0022);
});
function look(dx, dy, k) {
  state.yaw -= dx * k; state.pitch -= dy * k;
  state.pitch = Math.max(-1.25, Math.min(1.25, state.pitch));
}
let drag = null;
canvas.addEventListener('pointerdown', (e) => {
  if (state.overlay) return;
  if (e.pointerType === 'mouse' && !state.dragLook) return;
  if (e.pointerType === 'touch' && joyId === e.pointerId) return;
  drag = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: 0, t: performance.now() };
  canvas.setPointerCapture(e.pointerId);
});
canvas.addEventListener('pointermove', (e) => {
  if (!drag || e.pointerId !== drag.id) {
    if (e.pointerType === 'mouse' && state.dragLook && !state.overlay) state.mouse = [e.clientX, e.clientY];
    return;
  }
  const dx = e.clientX - drag.x, dy = e.clientY - drag.y; drag.x = e.clientX; drag.y = e.clientY; drag.moved += Math.abs(dx) + Math.abs(dy);
  look(dx, dy, e.pointerType === 'touch' ? 0.0045 : 0.0035);
  if (e.pointerType === 'mouse') state.mouse = [e.clientX, e.clientY];
});
canvas.addEventListener('pointerup', (e) => {
  if (!drag || e.pointerId !== drag.id) return;
  const tap = drag.moved < 8 && performance.now() - drag.t < 450; drag = null;
  if (tap) {
    const r = canvas.getBoundingClientRect();
    const p = pick(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1, IS_TOUCH ? 5.5 : 4.6);
    if (p) { state.inspectStart = p; openInspect(p); }
  }
});
canvas.addEventListener('pointercancel', () => { drag = null; });
canvas.addEventListener('click', () => {
  if (state.overlay || IS_TOUCH || state.dragLook) return;
  if (!state.locked) { requestLock(); return; }
  if (state.focus) { state.inspectStart = state.focus; openInspect(state.focus); }
});

/* touch joystick */
let joyId = null;
const joy = $('#joy'), knob = $('#joy-knob');
joy.addEventListener('pointerdown', (e) => { joyId = e.pointerId; joy.setPointerCapture(e.pointerId); moveJoy(e); });
joy.addEventListener('pointermove', (e) => { if (e.pointerId === joyId) moveJoy(e); });
const endJoy = (e) => { if (e.pointerId !== joyId) return; joyId = null; state.joy = [0, 0]; knob.style.transform = ''; };
joy.addEventListener('pointerup', endJoy); joy.addEventListener('pointercancel', endJoy);
function moveJoy(e) {
  const r = joy.getBoundingClientRect(), cx = r.left + r.width / 2, cy = r.top + r.height / 2, max = r.width / 2 - 18;
  let dx = e.clientX - cx, dy = e.clientY - cy; const d = Math.hypot(dx, dy); if (d > max) { dx *= max / d; dy *= max / d; }
  knob.style.transform = `translate(${dx}px, ${dy}px)`;
  state.joy = [dx / max, -dy / max];
}

/* keyboard */
addEventListener('keydown', (e) => {
  if (e.target instanceof HTMLInputElement) { if (e.key === 'Escape') closeOverlay(); return; }
  if (state.overlay === 'inspect') {
    if (e.key === 'ArrowRight') stepInspect(1); else if (e.key === 'ArrowLeft') stepInspect(-1); else if (e.key === 'Escape' || e.key === 'Enter') closeInspect();
    return;
  }
  if (state.overlay === 'directory') { if (e.key === 'Escape' || e.code === 'KeyM') closeOverlay(); return; }
  if (state.overlay === 'intro') return;
  if (e.code === 'KeyM' || e.code === 'Tab') { e.preventDefault(); openDirectory(); return; }
  if ((e.code === 'KeyE' || e.code === 'Enter' || e.code === 'Space') && state.focus) { e.preventDefault(); state.inspectStart = state.focus; openInspect(state.focus); return; }
  if (e.code.startsWith('Arrow') || e.code === 'Space') e.preventDefault();
  state.keys.add(e.code);
});
addEventListener('keyup', (e) => state.keys.delete(e.code));
addEventListener('blur', () => state.keys.clear());

/* buttons */
$('#btn-dir').addEventListener('click', () => openDirectory());
$('#dir-close').addEventListener('click', () => closeOverlay());
$('#directory').addEventListener('click', (e) => { if (e.target.id === 'directory') closeOverlay(); });
$('#lb-close').addEventListener('click', () => closeInspect());
$('#lb-prev').addEventListener('click', () => stepInspect(-1));
$('#lb-next').addEventListener('click', () => stepInspect(1));
$('#inspect').addEventListener('click', (e) => { if (e.target.id === 'inspect' || e.target.classList.contains('inspect-wrap')) closeInspect(); });
$('#find').addEventListener('submit', (e) => {
  e.preventDefault();
  const res = findDrawing($('#find-no').value);
  if (res.p) { closeOverlay(); fadeTo(() => { standBefore(res.p); }); return; }
  $('#find-msg').textContent = res.msg + (res.near ? ` The next one up is No. ${res.near.n}.` : '');
});

/* sound: a quiet room tone and the fire, synthesised */
let audio = null;
function startAudio() {
  const AC = window.AudioContext || window.webkitAudioContext; if (!AC) return null;
  const ctx = new AC(), sr = ctx.sampleRate;
  const noise = (sec, brown) => { const b = ctx.createBuffer(1, sr * sec, sr), d = b.getChannelData(0); let last = 0; for (let i = 0; i < d.length; i++) { const w = Math.random() * 2 - 1; if (brown) { last = (last + 0.02 * w) / 1.02; d[i] = last * 3.2; } else d[i] = w; } return b; };
  const master = ctx.createGain(); master.gain.value = 0.9; master.connect(ctx.destination);
  const tone = ctx.createBufferSource(); tone.buffer = noise(5, true); tone.loop = true;
  const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 320;
  const tg = ctx.createGain(); tg.gain.value = 0.05; tone.connect(lp).connect(tg).connect(master); tone.start();
  const fire = ctx.createGain(); fire.gain.value = 0; fire.connect(master);
  const roar = ctx.createBufferSource(); roar.buffer = noise(4, true); roar.loop = true;
  const rl = ctx.createBiquadFilter(); rl.type = 'lowpass'; rl.frequency.value = 700; const rg = ctx.createGain(); rg.gain.value = 0.22;
  roar.connect(rl).connect(rg).connect(fire); roar.start();
  const click = noise(0.05, false);
  const timer = setInterval(() => {
    if (ctx.state !== 'running' || Math.random() > 0.55) return;
    const s = ctx.createBufferSource(); s.buffer = click;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 1200 + Math.random() * 3200; bp.Q.value = 2;
    const g = ctx.createGain(); const t = ctx.currentTime; g.gain.setValueAtTime(0.25 + Math.random() * 0.5, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.03 + Math.random() * 0.05);
    s.connect(bp).connect(g).connect(fire); s.start(t + Math.random() * 0.05);
  }, 70);
  return { ctx, fire, timer };
}
$('#btn-sound').addEventListener('click', () => {
  const b = $('#btn-sound');
  if (!audio) { audio = startAudio(); if (!audio) { b.textContent = 'No sound here'; return; } }
  else if (audio.ctx.state === 'running') { audio.ctx.suspend(); b.textContent = 'Sound off'; b.setAttribute('aria-pressed', 'false'); return; }
  else audio.ctx.resume();
  b.textContent = 'Sound on'; b.setAttribute('aria-pressed', 'true');
});
function updateAudio() {
  if (!audio || audio.ctx.state !== 'running') return;
  const f = world.salon?.fire; const d = f ? camera.position.distanceTo(f) : 99;
  audio.fire.gain.setTargetAtTime(Math.min(0.9, 2.2 / (1 + d * d * 0.12)), audio.ctx.currentTime, 0.3);
}

/* ------------------------------------------------------------------ frame loop */
function resize() {
  const w = canvas.clientWidth, h = canvas.clientHeight;
  renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
  const scale = (h * Q.dpr) / (2 * Math.tan((camera.fov * Math.PI) / 360));
  if (M.glowHalo) { M.glowHalo.uniforms.uScale.value = scale; M.glowFlame.uniforms.uScale.value = scale; }
}
addEventListener('resize', resize);
let last = performance.now();
function tick(now) {
  const dt = Math.min(0.05, (now - last) / 1000); last = now;
  const t = now / 1000;
  if (!state.ready) { requestAnimationFrame(tick); return; }
  if (state.started && !state.overlay) updateMovement(dt);
  else if (!state.started) { state.yaw = Math.sin(t * 0.12) * 0.18; camera.rotation.set(-0.02, state.yaw, 0, 'YXZ'); }
  updateRoomVisibility();
  processBuildQueue();
  if (now - state.lastTick > 220) {
    state.lastTick = now; updateLights(); updateStreaming(); updateAudio();
    if (world.long && state.room === world.long.index) { const sec = longSection(); if (sec && sec.key !== state.hudKey) updateHUD(); }
    if (state.started && !state.overlay) {
      let p = null;
      if (state.locked || IS_TOUCH) p = IS_TOUCH ? null : pick(0, 0);
      else if (state.mouse) { const r = canvas.getBoundingClientRect(); p = pick(((state.mouse[0] - r.left) / r.width) * 2 - 1, -((state.mouse[1] - r.top) / r.height) * 2 + 1); }
      setFocus(p);
    }
  }
  flickerLights(t);
  M.glowHalo.uniforms.uTime.value = t; M.glowFlame.uniforms.uTime.value = t;
  renderer.render(scene, camera);
  requestAnimationFrame(tick);
}

/* ------------------------------------------------------------------ boot */
async function boot(saved) {
  try {
    setStatus('Unlocking the doors…', 0.03);
    const fontsReady = Promise.race([
      Promise.all(['56px "IM Fell English SC"', 'italic 56px "IM Fell English"', '40px "Special Elite"', '40px "Spectral"'].map((f) => document.fonts.load(f))),
      new Promise((r) => setTimeout(r, 4000)),
    ]);
    const [data, early] = await Promise.all([
      fetch(ASSET('data/layout.json')).then((r) => { if (!r.ok) throw new Error('layout ' + r.status); return r.json(); }),
      Promise.resolve(null),
    ]);
    planMuseum(data, early);
    $('#st-count').textContent = fmt(world.numbered.length);
    $('#st-early').textContent = fmt(world.early.length);
    $('#st-early').parentElement.hidden = !world.early.length;
    $('#st-rooms').textContent = String(world.rooms.filter((r) => r.kind !== 'vest').length);
    setStatus('Planing walnut and mahogany…', 0.1);
    await buildMaterials((f) => setStatus('Planing walnut and mahogany…', 0.1 + f * 0.5));
    await fontsReady;
    buildModels(); setupLights(); resize();
    setStatus('Lighting the candles…', 0.65);
    { const s = spawnOf(world.salon); camera.position.set(s.x, EYE, s.z); state.yaw = s.yaw; }
    if (saved && typeof saved.x === 'number') { camera.position.set(saved.x, EYE, saved.z); state.yaw = saved.yaw || 0; state.pitch = saved.pitch || 0; }
    buildRoom(world.salon); state.ready = true; updateRoomVisibility(); await nextFrame();
    for (const r of world.rooms) if (!r.built && r.near < 42) { buildRoom(r); await nextFrame(); }
    updateRoomVisibility(); updateLights(); updateStreaming();
    setStatus('Hanging the first drawings…', 0.85);
    const firstLo = [...new Set(world.salon.chunks.map((c) => c.lo))];
    const until = performance.now() + 6000;
    while (firstLo.some((i) => world.loAtlases[i].state !== 'ready') && performance.now() < until) await new Promise((r) => setTimeout(r, 100));
    Promise.all([
      fetch(ASSET('data/labels.json')).then((r) => r.json()),
      world.early.length ? fetch(ASSET('data/early-labels.json')).then((r) => r.json()).catch(() => ({})) : {},
    ]).then(([a, b]) => { world.labels = { ...b, ...a }; }).catch(() => {});
    setStatus('The museum is open.', 1);
    const btn = $('#enter'); btn.disabled = false; btn.focus({ preventScroll: true });
    const hashN = (location.hash || '').replace('#', '');
    btn.addEventListener('click', () => {
      $('#intro').classList.add('leaving'); setTimeout(() => { $('#intro').hidden = true; }, 600);
      $('#hud').hidden = false; $('#joy').hidden = !IS_TOUCH; $('#crosshair').hidden = true;
      state.started = true; state.overlay = null;
      if (!saved || typeof saved.x !== 'number') { state.yaw = spawnOf(world.salon).yaw; state.pitch = 0; }
      if (/^early$/i.test(hashN) && world.rooms.find((r) => r.early)) goToRoom(world.rooms.find((r) => r.early));
      else if (/^\d{1,6}$/.test(hashN)) {
        const n = hashN.padStart(6, '0');
        const yr = world.long?.years.find((y) => String(y.year) === hashN);
        if (yr) goTo(world.long, { x: 0, z: yr.z - 0.8, yaw: 0 }); else if (world.byN.get(n)) standBefore(world.byN.get(n));
      }
      updateHUD(); requestLock(); showLookHint();
    }, { once: true });
  } catch (err) {
    console.error(err);
    setStatus('The doors are stuck: ' + (err.message || err) + '. Reload the page to try again.', 0);
  }
}
const hot = window.claude?.hot;
try { hot?.snapshot?.(() => ({ x: camera.position.x, z: camera.position.z, yaw: state.yaw, pitch: state.pitch })); } catch {}
requestAnimationFrame(function first(now) { last = now; requestAnimationFrame(tick); });
const start = (data) => boot(data || {});
hot?.ready ? hot.ready(start) : start(hot?.data ?? {});
